
import { create } from 'zustand';
import { AppState, Role, Project, Phase, Task, User, TaskStatus, PhaseStatus, FileAsset, Notification, Ticket, TaskTemplate, Template, TemplatePhase, AlertPrefs, Comment, ThemeMode, PendingUser, CustomCalendar, CalendarEvent, ProjectRole, TaskVisibility } from './types';
import { useMockData, isFirebaseEnabled } from './lib/firebase/config';
import * as firebaseServices from './lib/firebase/services';
import * as firebaseAuth from './lib/firebase/auth';
import { onAuthStateChange } from './lib/firebase/auth';
import { propagateDateChanges, normalizeProjectName, isDuplicateProjectName } from './lib/utils';
import type { Unsubscribe } from 'firebase/firestore';

export const CELL_WIDTH = 320; 
export const CELL_HEIGHT = 200; 

const generateId = (prefix: string = 'id') => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`;

// Deduplicate projects by ID (keeps first occurrence)
const deduplicateProjects = (projects: Project[]): Project[] => {
  const seen = new Map<string, Project>();
  projects.forEach(p => {
    if (!seen.has(p.id)) {
      seen.set(p.id, p);
    }
  });
  return Array.from(seen.values());
};

// Generic utility to remove undefined values from any object before Firebase save
// Firebase Firestore rejects documents with undefined field values
const removeUndefinedFields = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const cleaned: any = {};
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (value !== undefined) {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

// Helper to remove undefined values from task data before Firebase save
// CRITICAL: For template tasks, projectId must be explicitly null (not undefined or missing)
const cleanTaskForFirebase = (task: Partial<Task>): Partial<Task> => {
  const cleaned = removeUndefinedFields(task);
  
  // CRITICAL FIX: If this is a template task (has templateId but no projectId),
  // ensure projectId is explicitly set to null for Firestore security rules
  if ((cleaned as any).templateId && !(cleaned as any).projectId) {
    (cleaned as any).projectId = null;
  }
  
  // Remove client-only fields before sending to Firebase
  delete (cleaned as any)._firebaseSynced;
  delete (cleaned as any)._localOnly;
  
  return cleaned;
};

// Sync State Tracking Helpers

/**
 * Create a new task marked as unsynced to Firebase
 */
const createUnsyncedTask = (prefix: string, data: Partial<Task>): Task => {
  return {
    ...data,
    id: generateId(prefix),
    _firebaseSynced: false,
  } as Task;
};

/**
 * Mark a task as synced with Firebase and update its ID
 */
const markTaskAsSynced = (task: Task, firebaseId: string): Task => ({
  ...task,
  id: firebaseId,
  _firebaseSynced: true,
});

/**
 * Filter tasks that are confirmed to exist in Firebase
 * Excludes tasks with temporary IDs or unsynced flag
 */
const filterSyncedTasks = (tasks: Task[]): Task[] =>
  tasks.filter(t => 
    t._firebaseSynced !== false && 
    !t.id.match(/^(nt|t|st)-/)
  );

/**
 * Check if a task is synced to Firebase
 */
const isTaskSynced = (task: Task): boolean =>
  task._firebaseSynced === true || 
  (task._firebaseSynced !== false && !task.id.match(/^(nt|t|st)-/));

// Optimistic Update Wrapper
// This wrapper enables optimistic UI updates with automatic rollback on failure

/**
 * Execute an operation with optimistic UI update and rollback on failure
 * @param operationId Unique identifier for this operation
 * @param optimisticUpdate Function to apply optimistic state change
 * @param firebaseOperation Async function to execute Firebase operation
 * @param rollback Function to revert optimistic changes on failure
 * @param onSuccess Optional callback after successful Firebase operation
 * @returns Result of Firebase operation or null on failure
 */
const withOptimisticUpdate = async <T,>(
  operationId: string,
  optimisticUpdate: () => void,
  firebaseOperation: () => Promise<T>,
  rollback: () => void,
  onSuccess?: (result: T) => void
): Promise<T | null> => {
  const get = () => useAppStore.getState();
  const set = useAppStore.setState;
  
  try {
    // 1. Apply optimistic update immediately for responsive UI
    optimisticUpdate();
    
    // 2. Track pending operation
    set(state => ({
      pendingOperations: [...state.pendingOperations, { 
        id: operationId, 
        type: 'update' as const,
        entity: 'task' as const,
        rollback,
        firebaseOperation 
      }],
      operationMetrics: {
        ...state.operationMetrics,
        pending: state.operationMetrics.pending + 1
      }
    }));
    
    // 3. Execute Firebase operation
    const result = await firebaseOperation();
    
    // 4. Apply success callback (e.g., update ID from Firebase)
    if (onSuccess) {
      onSuccess(result);
    }
    
    // 5. Remove from pending and update metrics
    set(state => ({
      pendingOperations: state.pendingOperations.filter(op => op.id !== operationId),
      operationMetrics: {
        succeeded: state.operationMetrics.succeeded + 1,
        failed: state.operationMetrics.failed,
        pending: state.operationMetrics.pending - 1
      }
    }));
    
    return result;
  } catch (error: any) {
    // 6. Rollback optimistic changes
    console.error(`Operation ${operationId} failed:`, error);
    rollback();
    
    // 7. Remove from pending and update metrics
    set(state => ({
      pendingOperations: state.pendingOperations.filter(op => op.id !== operationId),
      operationMetrics: {
        succeeded: state.operationMetrics.succeeded,
        failed: state.operationMetrics.failed + 1,
        pending: state.operationMetrics.pending - 1
      }
    }));
    
    // 8. Show error notification
    get().addNotification(
      `Failed: ${error.message || 'Unknown error'}`,
      'error'
    );
    
    return null;
  }
};

// Cache Configuration
const CACHE_TTL = 300000; // 5 minutes
const MAX_CACHE_ENTRIES = {
  tasks: 20, // Keep last 20 project/template task sets
  projects: 50, // Keep last 50 projects
  templates: 10, // Keep last 10 templates
  files: 30, // Keep last 30 file sets
};

// Cache Types
type CacheEntry<T> = {
  data: T;
  timestamp: number;
  lastAccessed: number; // For LRU tracking
  source: 'assigned' | 'project' | 'template'; // Track data source
};

type DataCache = {
  tasks: Map<string, CacheEntry<Task[]>>;
  projects: Map<string, CacheEntry<Project>>;
  templates: Map<string, CacheEntry<Template>>;
  files: Map<string, CacheEntry<FileAsset[]>>;
  hitCount: number; // Metrics
  missCount: number;
};

type SubscriptionInfo = {
  unsubscribe: () => void;
  context: string;
  coveredTaskIds?: Set<string>; // Track which tasks this subscription covers
};

// Cache Utilities
const isCacheValid = (timestamp: number, ttl = CACHE_TTL) => {
  return Date.now() - timestamp < ttl;
};

export const MOCK_USERS: User[] = [
  { id: 'u1', name: 'Alice Chen', email: 'alice@brand.com', emailVerified: true, role: Role.ADMIN, avatar: '', company: 'LuxeFashion', department: 'PMO', assignedProjectIds: ['p1'], calendarSyncToken: 'st-alice-' + Math.random().toString(36).substring(7) },
  { id: 'u2', name: 'Bob Smith', email: 'bob@factory.com', emailVerified: false, role: Role.FACTORY, avatar: '', company: 'GlobalTex', department: 'Operations', calendarSyncToken: 'st-bob-' + Math.random().toString(36).substring(7) },
  { id: 'u3', name: 'John Smith', email: 'john@factory.com', emailVerified: true, role: Role.FACTORY, avatar: '', company: 'GlobalTex', department: 'Operations' },
  { id: 'u4', name: 'Sarah Thompson', email: 'sarah@brand.com', emailVerified: true, role: Role.PM, avatar: '', company: 'LuxeFashion', department: 'Design' },
  { id: 'u5', name: 'Admin User', email: 'admin@brand.com', emailVerified: true, role: Role.ADMIN, avatar: '', company: 'LuxeFashion', department: 'IT' },
];

export const MOCK_PENDING_USERS: PendingUser[] = [
  { id: 'pu1', name: 'Marco Rossi', email: 'marco.r@leatherworks.it', avatar: '', requestedAt: Date.now() - 172800000, source: 'Invite Link' },
  { id: 'pu2', name: 'Elena Vostova', email: 'elena.v@globaltex.cn', avatar: '', requestedAt: Date.now() - 86400000, source: 'SSO Portal' },
  { id: 'pu3', name: 'David Kim', email: 'david.k@brand.com', avatar: '', requestedAt: Date.now() - 3600000, source: 'Domain Signup' }
];

export const MOCK_PROJECT_ROLES: ProjectRole[] = [
  { id: 'role-1', name: 'Product Manager', organization: 'Brand', team: 'PMO', createdAt: Date.now() },
  { id: 'role-2', name: 'Designer', organization: 'Brand', team: 'Design', createdAt: Date.now() },
  { id: 'role-3', name: 'QA Lead', organization: 'Factory', team: 'Quality', createdAt: Date.now() },
  { id: 'role-4', name: 'Factory Manager', organization: 'Factory', team: 'Factory Ops', createdAt: Date.now() },
  { id: 'role-5', name: 'Sourcing Specialist', organization: 'Brand', team: 'Sourcing', createdAt: Date.now() },
];

export const MOCK_TEMPLATES: TaskTemplate[] = [
  { name: 'Initial Pattern Grading', avgDays: 4.2, count: 12 },
  { name: 'Material Stress Test', avgDays: 7.5, count: 8 },
  { name: 'Color Handoff', avgDays: 2.1, count: 25 },
  { name: 'Final Tech Pack Review', avgDays: 3.4, count: 15 }
];

export const MOCK_PROJECTS: Project[] = [
  { id: 'p1', name: 'Summer Collection 2025', code: 'SC25', brand: 'LuxeFashion', factory: 'GlobalTex', brandPMId: 'u1', factoryPMId: 'u2', status: 'Green', progress: 45, startDate: '2024-01-10', launchDate: '2024-06-20', activePhaseId: 'ph2', isPrivate: false },
];

export const MOCK_PHASES: Phase[] = [
  { id: 'ph1', name: 'Design', status: PhaseStatus.COMPLETED, projectId: 'p1', gatePassed: true, isPrivate: true },
  { id: 'ph2', name: 'Build', status: PhaseStatus.ACTIVE, projectId: 'p1', gatePassed: false, isPrivate: false },
];

export const MOCK_TICKETS: Ticket[] = [
  {
    id: 't1',
    title: 'Sample Material Approval',
    description: 'We need the final approval on the recycled polyester swatch before bulk order.',
    status: 'OPEN',
    priority: 'HIGH',
    requesterId: 'u2',
    assigneeId: 'u1',
    projectId: 'p1',
    createdAt: Date.now() - 86400000,
    updatedAt: Date.now() - 86400000,
  },
  {
    id: 't2',
    title: 'Update Production Timeline',
    description: 'Factory holiday shift requires a 2-day buffer adjustment in the Build phase.',
    status: 'IN_PROGRESS',
    priority: 'MEDIUM',
    requesterId: 'u1',
    assigneeId: 'u2',
    projectId: 'p1',
    createdAt: Date.now() - 172800000,
    updatedAt: Date.now() - 43200000,
  }
];

export const MOCK_FILES: FileAsset[] = [
  { id: 'f1', name: 'front-view-render.png', type: 'image', category: 'Render', size: '4.2MB', uploadedBy: 'Alice Chen', uploadDate: '2025-01-12', tags: ['final', 'v1'], projectId: 'p1', audience: 'All', thumbnailUrl: 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?auto=format&fit=crop&w=300&q=80' },
  { id: 'f2', name: 'isometric-packaging-concept.jpg', type: 'image', category: 'Packaging', size: '2.1MB', uploadedBy: 'Alice Chen', uploadDate: '2025-01-14', tags: ['box', 'concept'], projectId: 'p1', audience: 'All', thumbnailUrl: 'https://images.unsplash.com/photo-1589939705384-5185138a047a?auto=format&fit=crop&w=300&q=80' },
  { id: 'f3', name: 'assembly-guide-v1.pdf', type: 'document', category: 'Manual', size: '1.2MB', uploadedBy: 'Bob Smith', uploadDate: '2025-01-15', tags: ['factory', 'guide'], projectId: 'p1', audience: 'All', thumbnailUrl: 'https://images.unsplash.com/photo-1589939705384-5185138a047a?auto=format&fit=crop&w=300&q=80' },
];

const getTreeTasks = (tasks: Task[], startId: string): Set<string> => {
  const visited = new Set<string>();
  const stack = [startId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (visited.has(id)) continue;
    visited.add(id);
    const t = tasks.find(item => item.id === id);
    if (!t) continue;
    if (t.childBottomId) stack.push(t.childBottomId);
    t.childLeftIds.forEach(cid => stack.push(cid));
    t.childRightIds.forEach(cid => stack.push(cid));
    tasks.forEach(other => {
        if (other.childBottomId === id || other.childLeftIds.includes(id) || other.childRightIds.includes(id)) {
            stack.push(other.id);
        }
    });
  }
  return visited;
};

const getDescendants = (tasks: Task[], startId: string): Set<string> => {
  const descendants = new Set<string>();
  const stack = [startId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (descendants.has(id)) continue;
    if (id !== startId) descendants.add(id);
    const t = tasks.find(item => item.id === id);
    if (!t) continue;
    if (t.childBottomId) stack.push(t.childBottomId);
    t.childLeftIds.forEach(cid => stack.push(cid));
    t.childRightIds.forEach(cid => stack.push(cid));
  }
  return descendants;
};

/**
 * Tree-aware relayout: recalculates x,y positions for all tasks in a scope
 * based purely on the tree structure (childBottomId, childLeftIds, childRightIds).
 * Uses a two-pass approach:
 *   Pass 1: lay out each tree at origin (x=0) to compute its width
 *   Pass 2: place trees side-by-side, offsetting by accumulated width
 */
const relayoutScope = (allTasks: Task[], phaseId: string, parentId?: string): Task[] => {
  const scopeTasks = allTasks.filter(t =>
    t.phaseId === phaseId &&
    (parentId ? t.parentId === parentId : !t.parentId)
  );
  if (scopeTasks.length === 0) return allTasks;

  const childIds = new Set<string>();
  scopeTasks.forEach(t => {
    if (t.childBottomId) childIds.add(t.childBottomId);
    t.childLeftIds.forEach(id => childIds.add(id));
    t.childRightIds.forEach(id => childIds.add(id));
  });
  const roots = scopeTasks
    .filter(t => !childIds.has(t.id))
    .sort((a, b) => a.x - b.x);

  if (roots.length === 0) return allTasks;

  // Lay out a single tree starting at baseX, y. Returns extent used.
  const layoutTree = (
    taskId: string,
    baseX: number,
    y: number,
    positions: Map<string, { x: number; y: number }>,
  ): { minX: number; maxX: number } => {
    const t = scopeTasks.find(item => item.id === taskId);
    if (!t || positions.has(taskId)) return { minX: baseX, maxX: baseX };

    positions.set(taskId, { x: baseX, y });
    let minX = baseX;
    let maxX = baseX;

    // Series child FIRST: compute columns used by the entire chain below.
    // This tells us which columns are off-limits for parallel children.
    if (t.childBottomId) {
      const ext = layoutTree(t.childBottomId, baseX, y - 1, positions);
      minX = Math.min(minX, ext.minX);
      maxX = Math.max(maxX, ext.maxX);
    }

    // Left children: first column left of seriesMinX
    for (const cid of [...t.childLeftIds].reverse()) {
      const lx = minX - 1;
      const ext = layoutTree(cid, lx, y, positions);
      minX = Math.min(minX, ext.minX);
    }

    // Right children: first column right of seriesMaxX
    for (const cid of t.childRightIds) {
      const rx = maxX + 1;
      const ext = layoutTree(cid, rx, y, positions);
      maxX = Math.max(maxX, ext.maxX);
    }

    return { minX, maxX };
  };

  // Pass 1: lay out each tree at origin, then place side-by-side
  const finalPositions = new Map<string, { x: number; y: number }>();
  let accX = 0;
  for (const root of roots) {
    const treePositions = new Map<string, { x: number; y: number }>();
    const { minX, maxX } = layoutTree(root.id, 0, 0, treePositions);
    const treeWidth = maxX - minX + 1;
    const offsetX = accX - minX;
    treePositions.forEach((pos, id) => {
      finalPositions.set(id, { x: pos.x + offsetX, y: pos.y });
    });
    accX += treeWidth;
  }

  // Pass 2: resolve (x,y) collisions within each tree.
  // When two tasks share the same position, shift the parallel branch right.
  const occupied = new Map<string, string>(); // "x,y" → taskId
  const shiftSubtree = (taskId: string, dx: number) => {
    const pos = finalPositions.get(taskId);
    if (!pos) return;
    pos.x += dx;
    const t = scopeTasks.find(item => item.id === taskId);
    if (!t) return;
    if (t.childBottomId) shiftSubtree(t.childBottomId, dx);
    t.childLeftIds.forEach(cid => shiftSubtree(cid, dx));
    t.childRightIds.forEach(cid => shiftSubtree(cid, dx));
  };

  // Walk the tree in layout order and resolve collisions
  const resolveCollisions = (taskId: string) => {
    const t = scopeTasks.find(item => item.id === taskId);
    if (!t) return;
    const pos = finalPositions.get(taskId);
    if (!pos) return;

    const key = `${pos.x},${pos.y}`;
    if (occupied.has(key) && occupied.get(key) !== taskId) {
      shiftSubtree(taskId, 1);
      resolveCollisions(taskId);
      return;
    }
    occupied.set(key, taskId);

    if (t.childBottomId) resolveCollisions(t.childBottomId);
    t.childLeftIds.forEach(cid => resolveCollisions(cid));
    t.childRightIds.forEach(cid => resolveCollisions(cid));
  };
  roots.forEach(root => resolveCollisions(root.id));

  return allTasks.map(t => {
    const pos = finalPositions.get(t.id);
    if (pos && (t.x !== pos.x || t.y !== pos.y)) {
      return { ...t, x: pos.x, y: pos.y };
    }
    return t;
  });
};

const generateUniqueName = (baseName: string, existingTasks: Task[], scope: { projectId?: string, phaseId: string, parentId?: string }) => {
  const scopeTasks = existingTasks.filter(t => 
    t.projectId === scope.projectId && 
    t.phaseId === scope.phaseId && 
    t.parentId === scope.parentId
  );
  
  let name = baseName;
  let counter = 1;
  const names = new Set(scopeTasks.map(t => t.title.toLowerCase()));
  
  while (names.has(name.toLowerCase())) {
    counter++;
    name = `${baseName} ${counter}`;
  }
  return name;
};

// INITIAL MOCK TASKS GENERATION
const today = new Date();
const format = (d: Date) => d.toISOString().split('T')[0];
const day2 = new Date(today); day2.setDate(today.getDate() - 2);
export const INITIAL_TASKS: Task[] = [
    {
      id: 'origin-ph2',
      x: 0, y: 0,
      childLeftIds: [], childRightIds: [], childBottomId: 'task-overdue',
      title: 'Project Kickoff',
      status: 'COMPLETE',
      phaseId: 'ph2', projectId: 'p1',
      isCritical: true, duration: 1, daysToComplete: 1,
      startDate: format(day2), endDate: format(day2),
      assigneeIds: ['u1'], comments: [], teams: ['Strategy'], audience: 'Both',
      dependsOn: [], lastActivityAt: Date.now()
    },
    {
      id: 'task-overdue',
      x: 0, y: -1,
      childLeftIds: [], childRightIds: [],
      title: 'Initial Concept Review',
      status: 'NOT_STARTED',
      phaseId: 'ph2', projectId: 'p1',
      isCritical: true, duration: 3, daysToComplete: 4,
      startDate: format(today), endDate: format(today),
      assigneeIds: ['u2'], comments: [], teams: ['Design'], audience: 'Both',
      dependsOn: ['origin-ph2'], lastActivityAt: Date.now()
    },
    {
      id: 'task-internal-budget',
      x: 1, y: 0,
      childLeftIds: [], childRightIds: [],
      title: 'Internal Budget Approval',
      status: 'COMPLETE',
      phaseId: 'ph1', projectId: 'p1', 
      isCritical: false, duration: 2, daysToComplete: 2,
      startDate: format(day2), endDate: format(day2),
      assigneeIds: ['u1'], comments: [], teams: ['Strategy'], audience: 'Brand',
      dependsOn: [], lastActivityAt: Date.now()
    }
];

// Operation queue types
type PendingOperation = {
  id: string;
  type: 'create' | 'update' | 'delete';
  entity: 'task' | 'phase' | 'project';
  rollback: () => void;
  firebaseOperation: () => Promise<any>;
};

type OperationMetrics = {
  succeeded: number;
  failed: number;
  pending: number;
};

type ExtendedAppState = AppState & { 
  mode: 'mock' | 'firebase';
  isLoading: boolean;
  isAuthLoading: boolean; // Track authentication initialization
  devModeLoggedIn: boolean;
  setDevModeLoggedIn: (loggedIn: boolean) => void;
  initializeFirebase: () => Promise<void>;
  loadUserData: () => Promise<void>;
  
  // Operation queue for optimistic updates
  pendingOperations: PendingOperation[];
  operationInProgress: boolean;
  operationMetrics: OperationMetrics;
  
  // Cache layer
  cache: DataCache;
  activeSubscriptions: Map<string, SubscriptionInfo>;
  
  // Smart loading methods
  loadEssentials: () => Promise<void>;
  loadProjectTasks: (projectId: string, phaseId?: string, forceRefresh?: boolean) => Promise<void>;
  loadTemplateTasks: (templateId: string, phaseId?: string, forceRefresh?: boolean) => Promise<void>;
  loadProjectFiles: (projectId: string) => Promise<void>;
  
  // Cache management
  getCacheAge: (key: string, type: 'tasks' | 'projects' | 'templates' | 'files') => number | null;
  isCacheFresh: (key: string, type: 'tasks' | 'projects' | 'templates' | 'files') => boolean;
  evictLRU: (type: 'tasks' | 'projects' | 'templates' | 'files') => void;
  clearCache: () => void;
  
  // Subscription management with deduplication
  subscribeToContext: (context: string, id: string, taskIds?: string[]) => void;
  unsubscribeFromContext: (contextKey: string) => void;
  cleanupRealtimeSync: () => void;
  
  // Metrics
  getCacheMetrics: () => { hitRate: number; totalHits: number; totalMisses: number };
};

export const useAppStore = create<ExtendedAppState>((set, get) => {
  // Initialize Firebase auth listener if enabled
  let unsubscribeAuth: (() => void) | null = null;
  
  const initializeFirebase = async () => {
    if (!isFirebaseEnabled()) return;

    set({ isAuthLoading: true });
    unsubscribeAuth = onAuthStateChange((user) => {
      set({ user, isAuthLoading: false });
      if (user) {
        // Load essentials only (assigned tasks, tickets, project metadata)
        get().loadEssentials();
      } else {
        get().cleanupRealtimeSync();
        get().clearCache();
        set({ projects: [], tasks: [], tickets: [], templates: [] });
      }
    });
  };

  // Note: initializeFirebase is called from App.tsx useEffect, not here
  // Removed duplicate initialization that was causing multiple auth listeners

  return {
  devModeLoggedIn: true, // Start logged in for quick dev access
  isAuthLoading: !useMockData, // Start true for Firebase mode, false for mock
  user: useMockData ? (get()?.devModeLoggedIn !== false ? MOCK_USERS[0] : null) : null,
  projects: useMockData ? MOCK_PROJECTS : [],
  phases: useMockData ? MOCK_PHASES : [],
  tasks: useMockData ? INITIAL_TASKS : [],
  templates: [],
  tickets: useMockData ? MOCK_TICKETS : [],
  files: useMockData ? MOCK_FILES : [],
  users: useMockData ? MOCK_USERS : [],
  pendingUsers: useMockData ? MOCK_PENDING_USERS : [],
  taskTemplates: MOCK_TEMPLATES,
  notifications: [],
  followedProjectIds: useMockData ? ['p1'] : [],
  alertPrefs: {
    enabled: true,
    emailEnabled: true,
    notifyMentions: true,
    notifyTickets: true,
    notifyImportantNotStarted: true,
    notifyUpcomingStart: true,
    notifyDueSoon: true,
    digestFrequency: 'IMMEDIATE',
    leadTimeDays: 5,
    slackThreshold: 2
  },
  themeMode: 'system',
  customCalendars: [
    {
      id: 'cal-1',
      name: 'Corporate Holidays 2025',
      description: 'Standard global office closures and factory holidays.',
      creatorId: 'u1',
      editorIds: ['u5'],
      viewerIds: ['u2', 'u3', 'u4'],
      updatedAt: Date.now(),
      events: [
        { 
          id: 'ev-1', 
          title: 'New Year Break', 
          details: 'Global closure', 
          startDate: '2025-01-01', 
          endDate: '2025-01-02', 
          startTime: '00:00', 
          endTime: '23:59', 
          collaboratorIds: ['USR:u1'], 
          color: 'bg-rose-500' 
        }
      ]
    }
  ],
  selectedCustomCalendarIds: [],
  projectRoles: useMockData ? MOCK_PROJECT_ROLES : [],
  mode: useMockData ? 'mock' : 'firebase',
  isLoading: false,
  
  // Operation queue
  pendingOperations: [],
  operationInProgress: false,
  operationMetrics: {
    succeeded: 0,
    failed: 0,
    pending: 0,
  },
  
  // Initialize cache
  cache: {
    tasks: new Map(),
    projects: new Map(),
    templates: new Map(),
    files: new Map(),
    hitCount: 0,
    missCount: 0,
  },
  activeSubscriptions: new Map(),

  setDevModeLoggedIn: (loggedIn: boolean) => {
    if (useMockData) {
      set({ 
        devModeLoggedIn: loggedIn,
        user: loggedIn ? MOCK_USERS[0] : null 
      });
    }
  },

  initializeFirebase,

  // Cache management utilities
  getCacheAge: (key: string, type: 'tasks' | 'projects' | 'templates' | 'files') => {
    const entry = get().cache[type].get(key);
    if (!entry) return null;
    return Math.floor((Date.now() - entry.timestamp) / 60000); // Return age in minutes
  },

  isCacheFresh: (key: string, type: 'tasks' | 'projects' | 'templates' | 'files') => {
    const entry = get().cache[type].get(key);
    if (!entry) return false;
    return isCacheValid(entry.timestamp);
  },

  evictLRU: (type: 'tasks' | 'projects' | 'templates' | 'files') => {
    const cache = get().cache[type];
    const maxSize = MAX_CACHE_ENTRIES[type];
    
    if (cache.size <= maxSize) return;
    
    // Find least recently accessed entry
    let oldestKey: string | null = null;
    let oldestTime = Infinity;
    
    cache.forEach((entry: CacheEntry<any>, key: string) => {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestKey = key;
      }
    });
    
    if (oldestKey) {
      console.log(`[CACHE] Evicting LRU entry: ${oldestKey} from ${type}`);
      cache.delete(oldestKey);
      
      // If it's a task cache, unsubscribe from that context
      if (type === 'tasks') {
        get().unsubscribeFromContext(oldestKey);
      }
    }
  },

  clearCache: () => {
    console.log('[CACHE] Clearing all cached data');
    get().cache.tasks.clear();
    get().cache.projects.clear();
    get().cache.templates.clear();
    get().cache.files.clear();
    set(state => ({
      cache: { ...state.cache, hitCount: 0, missCount: 0 }
    }));
  },

  getCacheMetrics: () => {
    const { hitCount, missCount } = get().cache;
    const total = hitCount + missCount;
    return {
      hitRate: total > 0 ? (hitCount / total) * 100 : 0,
      totalHits: hitCount,
      totalMisses: missCount,
    };
  },

  // Load only essentials on login (assigned tasks, tickets, project metadata)
  loadEssentials: async () => {
    const state = get();
    if (state.mode === 'mock' || !state.user) return;
    
    console.log('[SMART LOAD] Loading essentials for:', state.user.email);
    set({ isLoading: true });
    
    try {
      const userId = state.user.id;
      const userRole = state.user.role;
      
      // Determine if user can access templates (ADMIN or PM with company)
      const canAccessTemplates = userRole === Role.ADMIN || (userRole === Role.PM && state.user?.company);
      
      // Parallel load of essentials only
      const [myTasks, myTickets, projectList, users, templateList] = await Promise.all([
        firebaseServices.fetchMyAssignedTasks(userId), // Will add this function
        firebaseServices.fetchTickets(userId),
        firebaseServices.fetchProjects(userId, userRole), // Lightweight - just metadata
        firebaseServices.fetchUsers(),
        canAccessTemplates 
          ? firebaseServices.fetchTemplates().catch(err => {
              console.error('[SMART LOAD] Template fetch failed:', err);
              return [];
            })
          : Promise.resolve([]),
      ]);
      
      console.log('[SMART LOAD] Essentials loaded:', {
        tasks: myTasks.length,
        tickets: myTickets.length,
        projects: projectList.length,
        templates: templateList.length,
      });
      
      // Cache project metadata
      projectList.forEach(p => {
        state.cache.projects.set(p.id, { 
          data: p, 
          timestamp: Date.now(),
          lastAccessed: Date.now(),
          source: 'project',
        });
      });
      
      // Cache templates (similar to projects)
      templateList.forEach(t => {
        state.cache.templates.set(t.id, {
          data: t,
          timestamp: Date.now(),
          lastAccessed: Date.now(),
          source: 'essentials',
        });
      });
      
      // Cache assigned tasks (mark source for deduplication)
      const taskIds = new Set(myTasks.map(t => t.id));
      state.cache.tasks.set('my-assigned', {
        data: myTasks,
        timestamp: Date.now(),
        lastAccessed: Date.now(),
        source: 'assigned',
      });
      
      // Extract user preferences
      const currentUserDoc = users.find(u => u.id === userId);
      const followedProjectIds = currentUserDoc?.followedProjectIds || [];
      const alertPrefs = currentUserDoc?.alertPrefs || get().alertPrefs;
      const selectedCustomCalendarIds = currentUserDoc?.selectedCustomCalendarIds || [];
      const themeMode = currentUserDoc?.themeMode || 'system';
      
      set({
        projects: deduplicateProjects(projectList), // Deduplicate on load
        tasks: myTasks,
        tickets: myTickets,
        templates: templateList,
        users,
        followedProjectIds,
        alertPrefs,
        selectedCustomCalendarIds,
        themeMode,
        isLoading: false,
      });
      
      // Subscribe to my tickets and assigned tasks
      get().subscribeToContext('myTasks', userId, Array.from(taskIds));
      get().subscribeToContext('tickets', userId);
      
    } catch (error) {
      console.error('[SMART LOAD] Error loading essentials:', error);
      get().addNotification('Failed to load data', 'error');
      set({ isLoading: false });
    }
  },

  loadUserData: async () => {
    // Keep for backward compatibility, but use loadEssentials for new code
    await get().loadEssentials();
  },

  // Lazy load tasks for specific project/phase with cache validation
  loadProjectTasks: async (projectId: string, phaseId?: string, forceRefresh = false) => {
    const state = get();
    if (state.mode === 'mock') return;
    
    const cacheKey = `${projectId}-${phaseId || 'all'}`;
    const cached = state.cache.tasks.get(cacheKey);
    
    // Return cached if valid and not forcing refresh
    if (cached && isCacheValid(cached.timestamp) && !forceRefresh) {
      console.log('[SMART LOAD] Cache HIT:', cacheKey, `(${Math.floor((Date.now() - cached.timestamp) / 1000)}s old)`);
      
      // Update last accessed time for LRU
      cached.lastAccessed = Date.now();
      
      // Merge cached tasks with existing, deduplicating by ID
      set(state => {
        const taskMap = new Map<string, Task>();
        state.tasks.forEach(t => taskMap.set(t.id, t));
        cached.data.forEach(t => taskMap.set(t.id, t));
        
        return { 
          tasks: Array.from(taskMap.values()),
          cache: { ...state.cache, hitCount: state.cache.hitCount + 1 }
        };
      });
      
      // Still subscribe for real-time updates if not already subscribed
      const taskIds = cached.data.map(t => t.id);
      get().subscribeToContext('project', projectId, taskIds);
      return;
    }
    
    console.log('[SMART LOAD] Cache MISS:', cacheKey, forceRefresh ? '(forced refresh)' : '');
    
    try {
      const tasks = await firebaseServices.fetchTasks(projectId, phaseId);
      
      // Evict old entries if cache is full
      get().evictLRU('tasks');
      
      // Cache the result
      state.cache.tasks.set(cacheKey, {
        data: tasks,
        timestamp: Date.now(),
        lastAccessed: Date.now(),
        source: 'project',
      });
      
      // Merge with existing tasks, deduplicating
      set(state => {
        const taskMap = new Map<string, Task>();
        state.tasks.forEach(t => taskMap.set(t.id, t));
        tasks.forEach(t => taskMap.set(t.id, t));
        
        return { 
          tasks: Array.from(taskMap.values()),
          cache: { ...state.cache, missCount: state.cache.missCount + 1 }
        };
      });
      
      // Subscribe to real-time updates
      const taskIds = tasks.map(t => t.id);
      get().subscribeToContext('project', projectId, taskIds);
      
    } catch (error) {
      console.error('[SMART LOAD] Error loading project tasks:', error);
      get().addNotification('Failed to load tasks', 'error');
    }
  },

  // Lazy load tasks for specific template/phase with cache validation
  loadTemplateTasks: async (templateId: string, phaseId?: string, forceRefresh = false) => {
    const state = get();
    if (state.mode === 'mock') return;
    
    const cacheKey = `template-${templateId}-${phaseId || 'all'}`;
    const cached = state.cache.tasks.get(cacheKey);
    
    if (cached && isCacheValid(cached.timestamp) && !forceRefresh) {
      console.log('[SMART LOAD] Cache HIT:', cacheKey);
      cached.lastAccessed = Date.now();
      
      set(state => {
        const taskMap = new Map<string, Task>();
        state.tasks.forEach(t => taskMap.set(t.id, t));
        cached.data.forEach(t => taskMap.set(t.id, t));
        
        return { 
          tasks: Array.from(taskMap.values()),
          cache: { ...state.cache, hitCount: state.cache.hitCount + 1 }
        };
      });
      
      const taskIds = cached.data.map(t => t.id);
      get().subscribeToContext('template', templateId, taskIds);
      return;
    }
    
    console.log('[SMART LOAD] Cache MISS:', cacheKey);
    
    try {
      const tasks = await firebaseServices.fetchTasks(undefined, phaseId, undefined, templateId);
      
      get().evictLRU('tasks');
      
      state.cache.tasks.set(cacheKey, {
        data: tasks,
        timestamp: Date.now(),
        lastAccessed: Date.now(),
        source: 'template',
      });
      
      set(state => {
        const taskMap = new Map<string, Task>();
        state.tasks.forEach(t => taskMap.set(t.id, t));
        tasks.forEach(t => taskMap.set(t.id, t));
        
        return { 
          tasks: Array.from(taskMap.values()),
          cache: { ...state.cache, missCount: state.cache.missCount + 1 }
        };
      });
      
      const taskIds = tasks.map(t => t.id);
      get().subscribeToContext('template', templateId, taskIds);
      
    } catch (error) {
      console.error('[SMART LOAD] Error loading template tasks:', error);
    }
  },

  // Lazy load files for a project
  loadProjectFiles: async (projectId: string) => {
    const state = get();
    if (state.mode === 'mock') return;
    
    const cached = state.cache.files.get(projectId);
    
    if (cached && isCacheValid(cached.timestamp)) {
      console.log('[SMART LOAD] Cache HIT for files:', projectId);
      cached.lastAccessed = Date.now();
      
      set(state => ({
        files: cached.data,
        cache: { ...state.cache, hitCount: state.cache.hitCount + 1 }
      }));
      return;
    }
    
    try {
      const files = await firebaseServices.fetchFiles(projectId);
      
      get().evictLRU('files');
      
      state.cache.files.set(projectId, {
        data: files,
        timestamp: Date.now(),
        lastAccessed: Date.now(),
        source: 'project',
      });
      
      set(state => ({
        files,
        cache: { ...state.cache, missCount: state.cache.missCount + 1 }
      }));
    } catch (error) {
      console.error('[SMART LOAD] Error loading project files:', error);
    }
  },

  // Subscribe to real-time updates for specific context with deduplication
  subscribeToContext: (context: string, id: string, taskIds?: string[]) => {
    const state = get();
    if (state.mode === 'mock') return;
    
    const contextKey = `${context}-${id}`;
    
    // Check if already subscribed
    if (state.activeSubscriptions.has(contextKey)) {
      const existing = state.activeSubscriptions.get(contextKey)!;
      
      // If subscribing to tasks, check if these tasks are already covered
      if (taskIds && existing.coveredTaskIds) {
        const allCovered = taskIds.every(tid => existing.coveredTaskIds!.has(tid));
        if (allCovered) {
          console.log('[REALTIME] All tasks already covered by existing subscription:', contextKey);
          return;
        }
      } else {
        console.log('[REALTIME] Already subscribed to:', contextKey);
        return;
      }
    }
    
    console.log('[REALTIME] Subscribing to:', contextKey, taskIds ? `(${taskIds.length} tasks)` : '');
    
    let unsub: (() => void) | null = null;
    const coveredTaskIds = taskIds ? new Set(taskIds) : undefined;
    
    switch (context) {
      case 'project':
        unsub = firebaseServices.subscribeToTasks(id, undefined, (tasks) => {
          console.log('[REALTIME] Project tasks updated:', id, tasks.length);
          
          // Smart merge: deduplicate with existing tasks
          set(state => {
            const taskMap = new Map<string, Task>();
            
            // Start with existing tasks
            state.tasks.forEach(t => taskMap.set(t.id, t));
            
            // Overlay with new tasks (newer data wins)
            tasks.forEach(t => taskMap.set(t.id, t));
            
            return { tasks: Array.from(taskMap.values()) };
          });
          
          // Update cache
          const cacheKey = `${id}-all`;
          state.cache.tasks.set(cacheKey, {
            data: tasks,
            timestamp: Date.now(),
            lastAccessed: Date.now(),
            source: 'project',
          });
        });
        break;
        
      case 'template':
        unsub = firebaseServices.subscribeToTemplateTasks(id, (tasks) => {
          console.log('[REALTIME] Template tasks updated:', id, tasks.length);
          
          set(state => {
            const taskMap = new Map<string, Task>();
            state.tasks.forEach(t => taskMap.set(t.id, t));
            tasks.forEach(t => taskMap.set(t.id, t));
            return { tasks: Array.from(taskMap.values()) };
          });
        });
        break;
        
      case 'tickets':
        unsub = firebaseServices.subscribeToTickets(id, (tickets) => {
          console.log('[REALTIME] Tickets updated:', tickets.length);
          set({ tickets });
        });
        break;
        
      case 'myTasks':
        unsub = firebaseServices.subscribeToMyAssignedTasks(id, (tasks) => {
          console.log('[REALTIME] My assigned tasks updated:', tasks.length);
          
          // For assigned tasks, we need to be careful about deduplication
          // These might overlap with project subscriptions
          set(state => {
            const taskMap = new Map<string, Task>();
            
            // Keep existing tasks
            state.tasks.forEach(t => taskMap.set(t.id, t));
            
            // Update with assigned tasks (but only if not covered by project subscription)
            tasks.forEach(t => {
              // Check if this task is covered by an active project subscription
              const projectKey = `project-${t.projectId}`;
              const projectSub = state.activeSubscriptions.get(projectKey);
              
              if (!projectSub || !projectSub.coveredTaskIds?.has(t.id)) {
                // Not covered by project subscription, use this update
                taskMap.set(t.id, t);
              }
            });
            
            return { tasks: Array.from(taskMap.values()) };
          });
        });
        break;
    }
    
    if (unsub) {
      state.activeSubscriptions.set(contextKey, {
        unsubscribe: unsub,
        context: contextKey,
        coveredTaskIds,
      });
    }
  },

  // Unsubscribe from specific context
  unsubscribeFromContext: (contextKey: string) => {
    const sub = get().activeSubscriptions.get(contextKey);
    if (sub) {
      console.log('[REALTIME] Unsubscribing from:', contextKey);
      sub.unsubscribe();
      get().activeSubscriptions.delete(contextKey);
    }
  },

  // Clean up all subscriptions (on logout)
  cleanupRealtimeSync: () => {
    console.log('[REALTIME] Cleaning up all subscriptions');
    get().activeSubscriptions.forEach((sub) => sub.unsubscribe());
    get().activeSubscriptions.clear();
  },

  login: async (email: string, password?: string) => {
    if (get().mode === 'mock') {
      const user = get().users.find(u => u.email === email) || MOCK_USERS[0];
      set({ user });
      return;
    }

    if (!password) {
      get().addNotification('Password required for Firebase login', 'error');
      return;
    }

    try {
      set({ isLoading: true });
      const user = await firebaseAuth.signInWithEmail(email, password);
      set({ user, isLoading: false });
      await get().loadUserData();
    } catch (error: any) {
      get().addNotification(error.message || 'Login failed', 'error');
      set({ isLoading: false });
    }
  },

  logout: async () => {
    get().cleanupRealtimeSync();
    get().clearCache();
    
    if (get().mode === 'firebase') {
      try {
        await firebaseAuth.signOut();
      } catch (error: any) {
        console.error('Logout error:', error);
      }
    }
    set({ user: null, projects: [], tasks: [], tickets: [] });
    if (unsubscribeAuth) {
      unsubscribeAuth();
      unsubscribeAuth = null;
    }
  },

  updateUser: async (updates) => {
    const state = get();

    if (state.mode === 'firebase' && state.user) {
      try {
        await firebaseServices.updateUser(state.user.id, updates);
      } catch (error: any) {
        state.addNotification(error.message || 'Failed to update user', 'error');
        return;
      }
    }

    set(state => {
      const newUser = state.user ? { ...state.user, ...updates } : null;
      const newUsers = state.users.map(u => u.id === state.user?.id ? { ...u, ...updates } : u);
      
      // Sync user preferences to store state if they're being updated
      const preferenceUpdates: any = {};
      if ('followedProjectIds' in updates) {
        preferenceUpdates.followedProjectIds = updates.followedProjectIds;
      }
      if ('alertPrefs' in updates) {
        preferenceUpdates.alertPrefs = updates.alertPrefs;
      }
      if ('selectedCustomCalendarIds' in updates) {
        preferenceUpdates.selectedCustomCalendarIds = updates.selectedCustomCalendarIds;
      }
      if ('themeMode' in updates) {
        preferenceUpdates.themeMode = updates.themeMode;
      }
      
      return { 
        user: newUser, 
        users: newUsers,
        ...preferenceUpdates
      };
    });
  },

  updateProject: async (projectId, updates) => {
    // Remove undefined fields before updating Firebase
    const cleanedUpdates = removeUndefinedFields(updates);
    
    if (get().mode === 'mock') {
      set(state => ({
        projects: state.projects.map(p => p.id === projectId ? { ...p, ...cleanedUpdates } : p)
      }));
      return;
    }

    try {
      await firebaseServices.updateProject(projectId, cleanedUpdates);
      set(state => ({
        projects: state.projects.map(p => p.id === projectId ? { ...p, ...cleanedUpdates } : p)
      }));
    } catch (error: any) {
      get().addNotification(error.message || 'Failed to update project', 'error');
    }
  },

  archiveProject: async (projectId: string) => {
    const state = get();
    
    if (state.mode === 'firebase') {
      try {
        await firebaseServices.updateProject(projectId, {
          isArchived: true,
          archivedAt: Date.now()
        });
      } catch (error: any) {
        state.addNotification(error.message || 'Failed to archive project', 'error');
        return;
      }
    }
    
    // Update local state
    set(state => ({
      projects: state.projects.map(p =>
        p.id === projectId
          ? { ...p, isArchived: true, archivedAt: Date.now() }
          : p
      )
    }));
    
    state.addNotification('Project archived successfully', 'success');
  },

  deleteProject: async (projectId: string) => {
    const state = get();
    
    if (state.mode === 'firebase') {
      try {
        // Delete project from Firestore
        await firebaseServices.deleteProject(projectId);
        
        // Note: Related data (phases, tasks, files) should be handled:
        // Option 1: Delete via Firestore trigger (recommended)
        // Option 2: Delete manually here (more control)
        // For now, we'll rely on Firestore cascading rules or manual cleanup
      } catch (error: any) {
        state.addNotification(error.message || 'Failed to delete project', 'error');
        return;
      }
    }
    
    // Remove from local state
    set(state => ({
      projects: state.projects.filter(p => p.id !== projectId),
      phases: state.phases.filter(ph => ph.projectId !== projectId),
      tasks: state.tasks.filter(t => t.projectId !== projectId),
      files: state.files.filter(f => f.projectId !== projectId)
    }));
    
    state.addNotification('Project deleted permanently', 'success');
  },

  restoreProject: async (projectId: string) => {
    const state = get();
    
    if (state.mode === 'firebase') {
      try {
        await firebaseServices.updateProject(projectId, {
          isArchived: false,
          archivedAt: undefined
        });
      } catch (error: any) {
        state.addNotification(error.message || 'Failed to restore project', 'error');
        return;
      }
    }
    
    // Update local state
    set(state => ({
      projects: state.projects.map(p =>
        p.id === projectId
          ? { ...p, isArchived: false, archivedAt: undefined }
          : p
      )
    }));
    
    state.addNotification('Project restored successfully', 'success');
  },

  updatePhase: async (phaseId, updates) => {
    if (get().mode === 'firebase') {
      try {
        await firebaseServices.updatePhase(phaseId, updates);
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to update phase', 'error');
        return;
      }
    }
    
    set(state => ({
      phases: state.phases.map(p => p.id === phaseId ? { ...p, ...updates } : p)
    }));
  },

  setTasks: (tasks) => set({ tasks }),

  updateTask: async (taskId, updates) => {
    const state = get();
    const task = state.tasks.find(t => t.id === taskId);
    if (!task) return;

    // Check Duplicate Title
    if (updates.title && updates.title !== task.title) {
      const scopeTasks = state.tasks.filter(t => 
        t.id !== taskId && 
        t.projectId === task.projectId && 
        t.phaseId === task.phaseId && 
        t.parentId === task.parentId
      );
      const isDuplicate = scopeTasks.some(t => t.title.toLowerCase() === updates.title?.toLowerCase());
      if (isDuplicate) {
        state.addNotification(`A task named "${updates.title}" already exists in this timeline.`, "error");
        return;
      }
    }

    // Check Circular Dependency
    if (updates.dependsOn) {
      const isCircular = (id: string, target: string, visited = new Set<string>()): boolean => {
        if (id === target) return true;
        if (visited.has(id)) return false;
        visited.add(id);
        const t = state.tasks.find(item => item.id === id);
        if (!t || !t.dependsOn) return false;
        return t.dependsOn.some(depId => isCircular(depId, target, visited));
      };
      const hasCircle = updates.dependsOn.some(depId => isCircular(depId, taskId));
      if (hasCircle) {
        state.addNotification("Circular dependency detected! Action rejected.", "error");
        return;
      }
    }

    // Update Firebase if in Firebase mode
    if (state.mode === 'firebase') {
      try {
        await firebaseServices.updateTask(taskId, updates);
      } catch (error: any) {
        state.addNotification(error.message || 'Failed to update task', 'error');
        return;
      }
    }

    set(prev => {
      let next = prev.tasks.map(t => t.id === taskId ? { 
        ...t, 
        ...updates, 
        lastActivityAt: Date.now(),
        followUpDismissedAt: undefined 
      } : t);

      // Propagate date changes if lead time or due date changed
      if (updates.duration !== undefined || updates.endDate !== undefined) {
        next = propagateDateChanges(taskId, next);
        
        // Save propagated changes to Firebase if in Firebase mode (use batch update)
        if (state.mode === 'firebase') {
          const originalTasks = prev.tasks;
          const dateUpdates = next
            .filter(updatedTask => {
              const originalTask = originalTasks.find(t => t.id === updatedTask.id);
              return originalTask && 
                     updatedTask.endDate !== originalTask.endDate &&
                     isTaskSynced(updatedTask); // Only update synced tasks
            })
            .map(task => ({
              taskId: task.id,
              data: { endDate: task.endDate }
            }));
          
          if (dateUpdates.length > 0) {
            firebaseServices.batchUpdateTasks(dateUpdates).catch(err => 
              console.error('Failed to propagate date changes:', err)
            );
          }
        }
      }

      if (updates.hasSubtimeline === true && !task.hasSubtimeline) {
        const existingSubtasks = prev.tasks.filter(t => t.parentId === taskId);
        if (existingSubtasks.length === 0) {
          const subTaskName = generateUniqueName("Sub-start Node", prev.tasks, { projectId: task.projectId, phaseId: task.phaseId, parentId: taskId });
          const start = new Date().toISOString().split('T')[0];
          const newSubtask: Task = {
            id: generateId('st'), x: 0, y: 0, parentId: taskId, childLeftIds: [], childRightIds: [], title: subTaskName, audience: task.audience,
            status: 'NOT_STARTED', phaseId: task.phaseId, projectId: task.projectId, templateId: task.templateId, isCritical: false, dependsOn: [], startDate: start, endDate: start, lastActivityAt: Date.now(), duration: 1, daysToComplete: 1, visibility: TaskVisibility.PUBLIC
          };
          
          // Create in Firebase if needed
          if (state.mode === 'firebase') {
            firebaseServices.createTask(newSubtask).catch(err => console.error('Failed to create subtask:', err));
          }
          
          next.push(newSubtask);
        }
      }
      return { tasks: next };
    });
  },

  deleteTask: async (taskId) => {
    const state = get();
    const task = state.tasks.find(t => t.id === taskId);
    if (!task) return;

    // Collect all IDs to delete: the task itself + all descendants + subtimeline tasks
    const idsToDelete = new Set<string>([taskId]);

    // Recursively collect descendants (series/parallel children) and subtimeline tasks
    const collectAll = (id: string) => {
      const t = state.tasks.find(item => item.id === id);
      if (!t) return;
      if (t.childBottomId && !idsToDelete.has(t.childBottomId)) {
        idsToDelete.add(t.childBottomId);
        collectAll(t.childBottomId);
      }
      t.childLeftIds.forEach(cid => {
        if (!idsToDelete.has(cid)) { idsToDelete.add(cid); collectAll(cid); }
      });
      t.childRightIds.forEach(cid => {
        if (!idsToDelete.has(cid)) { idsToDelete.add(cid); collectAll(cid); }
      });
      // Subtimeline tasks: any task whose parentId is this task
      state.tasks.filter(sub => sub.parentId === id).forEach(sub => {
        if (!idsToDelete.has(sub.id)) { idsToDelete.add(sub.id); collectAll(sub.id); }
      });
    };
    collectAll(taskId);

    if (state.mode === 'firebase') {
      try {
        const syncedIds = Array.from(idsToDelete).filter(id => {
          const t = state.tasks.find(item => item.id === id);
          return t && isTaskSynced(t);
        });
        if (syncedIds.length > 0) {
          await firebaseServices.batchDeleteTasks(syncedIds);
        }
      } catch (error: any) {
        state.addNotification(error.message || 'Failed to delete task', 'error');
        return;
      }
    }

    set(state => {
      // Clean up parent references pointing to the deleted task
      let updatedTasks = state.tasks
        .filter(t => !idsToDelete.has(t.id))
        .map(t => {
          let changed = false;
          const updates: Partial<Task> = {};

          if (t.childBottomId && idsToDelete.has(t.childBottomId)) {
            updates.childBottomId = undefined;
            changed = true;
          }
          const newLeftIds = t.childLeftIds.filter(id => !idsToDelete.has(id));
          if (newLeftIds.length !== t.childLeftIds.length) {
            updates.childLeftIds = newLeftIds;
            changed = true;
          }
          const newRightIds = t.childRightIds.filter(id => !idsToDelete.has(id));
          if (newRightIds.length !== t.childRightIds.length) {
            updates.childRightIds = newRightIds;
            changed = true;
          }
          const newDeps = t.dependsOn?.filter(id => !idsToDelete.has(id));
          if (newDeps && newDeps.length !== (t.dependsOn?.length || 0)) {
            updates.dependsOn = newDeps;
            changed = true;
          }

          return changed ? { ...t, ...updates } : t;
        });

      // Full tree-aware relayout: recalculate positions from tree structure
      updatedTasks = relayoutScope(updatedTasks, task.phaseId, task.parentId);

      // Persist reference cleanups and position changes to Firebase
      if (state.mode === 'firebase') {
        const allUpdates = updatedTasks
          .filter(t => {
            const orig = state.tasks.find(o => o.id === t.id);
            if (!orig || !isTaskSynced(t)) return false;
            return orig.childBottomId !== t.childBottomId ||
                   orig.childLeftIds.length !== t.childLeftIds.length ||
                   orig.childRightIds.length !== t.childRightIds.length ||
                   orig.dependsOn?.length !== t.dependsOn?.length ||
                   orig.x !== t.x || orig.y !== t.y;
          })
          .map(t => ({
            taskId: t.id,
            data: {
              childBottomId: t.childBottomId || null,
              childLeftIds: t.childLeftIds,
              childRightIds: t.childRightIds,
              dependsOn: t.dependsOn || [],
              x: t.x,
              y: t.y,
            }
          }));
        if (allUpdates.length > 0) {
          firebaseServices.batchUpdateTasks(allUpdates).catch(err =>
            console.error('Failed to persist post-delete updates:', err)
          );
        }
      }

      // Invalidate task cache: purge deleted IDs from all cached task sets
      state.cache.tasks.forEach((entry, key) => {
        const filtered = entry.data.filter(t => !idsToDelete.has(t.id));
        if (filtered.length !== entry.data.length) {
          entry.data = filtered;
          entry.timestamp = Date.now();
        }
      });

      return { tasks: updatedTasks };
    });
    const count = idsToDelete.size;
    get().addNotification(count > 1 ? `Deleted ${count} nodes` : "Node archived", "info");
  },

  duplicateTask: async (taskId: string) => {
    const state = get();
    const originalTask = state.tasks.find(t => t.id === taskId);
    if (!originalTask) return;
    
    const operationId = generateId('op');
    
    // Get all descendants of the task
    const descendants = getDescendants(state.tasks, taskId);
    const tasksToClone = [originalTask, ...state.tasks.filter(t => descendants.has(t.id))];
    
    // Find existing copies to determine the next number
    const baseTitle = originalTask.title.replace(/\s*\(\d+\)$/, '');
    const existingCopies = state.tasks.filter(t => 
      t.phaseId === originalTask.phaseId && 
      t.parentId === originalTask.parentId &&
      t.title.startsWith(baseTitle)
    );
    
    // Calculate next number
    const copyNumbers = existingCopies
      .map(t => {
        const match = t.title.match(/\((\d+)\)$/);
        return match ? parseInt(match[1]) : 0;
      })
      .filter(n => n > 0);
    const nextNum = copyNumbers.length > 0 ? Math.max(...copyNumbers) + 1 : 1;
    
    // Create ID mapping for maintaining relationships
    const idMap = new Map<string, string>();
    tasksToClone.forEach(t => {
      idMap.set(t.id, generateId('nt'));
    });
    
    // Calculate the width of the task tree
    const xPositions = tasksToClone.map(t => t.x);
    const minX = Math.min(...xPositions);
    const maxX = Math.max(...xPositions);
    const treeWidth = maxX - minX + 1;
    
    // Calculate insertion position
    const insertionX = maxX + 1;
    
    // Track tasks that need position updates
    const tasksToUpdate: Task[] = [];
    state.tasks.forEach(t => {
      if (t.phaseId === originalTask.phaseId && 
          t.parentId === originalTask.parentId && 
          t.x >= insertionX) {
        tasksToUpdate.push({ ...t, x: t.x + treeWidth });
      }
    });
    
    // Clone all tasks with proper offset
    const clonedTasks: Task[] = tasksToClone.map((task) => {
      const newId = idMap.get(task.id)!;
      const isRoot = task.id === taskId;
      const relativeX = task.x - minX;
      const newX = insertionX + relativeX;
      
      return {
        ...task,
        id: newId,
        _firebaseSynced: false,
        title: isRoot ? `${baseTitle} (${nextNum})` : task.title,
        x: newX,
        childLeftIds: task.childLeftIds.map(id => idMap.get(id) || id),
        childRightIds: task.childRightIds.map(id => idMap.get(id) || id),
        childBottomId: task.childBottomId ? (idMap.get(task.childBottomId) || task.childBottomId) : undefined,
        dependsOn: isRoot ? originalTask.dependsOn : task.dependsOn?.map(id => idMap.get(id) || id),
        assigneeIds: [],
        assignedRoleIds: task.assignedRoleIds,
        comments: [],
        files: [],
        lastActivityAt: Date.now(),
        status: 'NOT_STARTED' as TaskStatus,
        startedAt: undefined,
        completedAt: undefined,
        startDate: undefined,
        endDate: undefined,
        visibility: task.visibility || TaskVisibility.PUBLIC
      };
    });
    
    // Store original state for rollback
    const originalTasks = [...state.tasks];
    
    // Optimistic update
    const optimisticUpdate = () => {
      set(state => ({
        tasks: [
          ...state.tasks.map(t => {
            if (t.id === taskId) {
              return { ...t, childRightIds: [...(t.childRightIds || []), idMap.get(taskId)!] };
            }
            if (originalTask.parentId && t.id === originalTask.parentId) {
              return { ...t, childRightIds: [...(t.childRightIds || []), idMap.get(taskId)!] };
            }
            const updated = tasksToUpdate.find(tu => tu.id === t.id);
            return updated || t;
          }),
          ...clonedTasks
        ]
      }));
    };
    
    // Firebase operation
    const firebaseOperation = async () => {
      if (state.mode !== 'firebase') return clonedTasks.map(t => t.id);
      
      // Clean cloned tasks for Firebase
      const cleanedClonedTasks = clonedTasks.map(t => {
        const { id, _firebaseSynced, _localOnly, ...taskData } = t;
        return cleanTaskForFirebase(taskData);
      });
      
      // Batch create all cloned tasks
      const firebaseTaskIds = await firebaseServices.batchCreateTasks(
        cleanedClonedTasks as Array<Omit<Task, 'id'>>
      );
      
      // Create mapping from local IDs to Firebase IDs
      const localToFirebaseMap = new Map<string, string>();
      clonedTasks.forEach((task, index) => {
        localToFirebaseMap.set(task.id, firebaseTaskIds[index]);
      });
      
      // Batch update original task and parent (if exists) + position shifts
      const updates = [
        { 
          taskId: taskId, 
          data: { 
            childRightIds: [
              ...(originalTask.childRightIds || []), 
              localToFirebaseMap.get(idMap.get(taskId)!)!
            ] 
          } 
        }
      ];
      
      if (originalTask.parentId) {
        const parent = state.tasks.find(t => t.id === originalTask.parentId);
        if (parent) {
          updates.push({
            taskId: originalTask.parentId,
            data: {
              childRightIds: [
                ...(parent.childRightIds || []),
                localToFirebaseMap.get(idMap.get(taskId)!)!
              ]
            }
          });
        }
      }
      
      // Add position updates for synced tasks only
      const syncedUpdates = filterSyncedTasks(tasksToUpdate).map(t => ({
        taskId: t.id,
        data: { x: t.x }
      }));
      
      await firebaseServices.batchUpdateTasks([...updates, ...syncedUpdates]);
      
      return { firebaseTaskIds, localToFirebaseMap };
    };
    
    // Success callback
    const onSuccess = (result: any) => {
      if (!result || !result.localToFirebaseMap) return;
      
      set(state => ({
        tasks: state.tasks.map(t => {
          const firebaseId = result.localToFirebaseMap.get(t.id);
          if (firebaseId) {
            return markTaskAsSynced(t, firebaseId);
          }
          return t;
        })
      }));
    };
    
    // Rollback
    const rollback = () => {
      set({ tasks: originalTasks });
    };
    
    const success = await withOptimisticUpdate(
      operationId,
      optimisticUpdate,
      firebaseOperation,
      rollback,
      onSuccess
    );
    
    if (success) {
      get().addNotification('Task duplicated successfully', 'success');
    }
  },

  addTaskDown: async (parentId) => {
    const state = get();
    const parent = state.tasks.find(t => t.id === parentId);
    if (!parent) return;
    
    const isTemplate = !!parent.templateId && !parent.projectId;
    const operationId = generateId('op');
    
    const targetY = parent.y - 1;
    const existingChildId = parent.childBottomId;
    let tasksToUpdate: Task[] = [];
    
    // Calculate position shifts: only shift the existing bottom child and ITS
    // descendants — NOT the parent's parallel children (they stay at parent.y)
    if (existingChildId) {
      const toShift = new Set<string>([existingChildId]);
      getDescendants(state.tasks, existingChildId).forEach(id => toShift.add(id));
      state.tasks.forEach(t => {
        if (toShift.has(t.id)) {
          tasksToUpdate.push({ ...t, y: t.y - 1 });
        }
      });
    }
    
    const uniqueTitle = generateUniqueName("New Series Task", state.tasks, { 
      projectId: parent.projectId, 
      phaseId: parent.phaseId, 
      parentId: parent.parentId 
    });
    
    const newNode = createUnsyncedTask('nt', {
      x: parent.x, 
      y: targetY, 
      phaseId: parent.phaseId, 
      projectId: parent.projectId, 
      templateId: parent.templateId, 
      parentId: parent.parentId,
      childLeftIds: [], 
      childRightIds: [], 
      childBottomId: existingChildId, 
      title: uniqueTitle, 
      status: 'NOT_STARTED', 
      isCritical: parent.isCritical, 
      audience: parent.audience || 'Both',
      dependsOn: [parent.id], 
      lastActivityAt: Date.now(), 
      duration: 1, 
      daysToComplete: 1, 
      visibility: TaskVisibility.PUBLIC
    });
    
    // Store original state for rollback
    const originalTasks = [...state.tasks];
    
    // Optimistic update
    const optimisticUpdate = () => {
      set(state => ({
        tasks: [
          ...state.tasks.map(t => {
            if (t.id === parentId) return { ...t, childBottomId: newNode.id };
            const updated = tasksToUpdate.find(tu => tu.id === t.id);
            return updated || t;
          }),
          newNode
        ]
      }));
    };
    
    // Firebase operation
    const firebaseOperation = async () => {
      if (state.mode !== 'firebase') return newNode.id;
      
      const { id, _firebaseSynced, _localOnly, ...taskData } = newNode;
      const cleanedData = cleanTaskForFirebase(taskData);
      
      if (isTemplate) {
        // Template: create task + update parent + update synced task positions
        const firebaseTaskId = await firebaseServices.createTask(cleanedData as Omit<Task, 'id'>);
        
        // Filter to only synced tasks to avoid "document not found" errors
        const syncedUpdates = filterSyncedTasks(tasksToUpdate);
        
        if (syncedUpdates.length > 0) {
          // Batch update parent + synced position changes
          await firebaseServices.batchUpdateTasks([
            { taskId: parentId, data: { childBottomId: firebaseTaskId } },
            ...syncedUpdates.map(t => ({ taskId: t.id, data: { y: t.y } }))
          ]);
        } else {
          // Just update parent if no synced siblings need position changes
          await firebaseServices.updateTask(parentId, { childBottomId: firebaseTaskId });
        }
        
        return firebaseTaskId;
      } else {
        // Project: use transaction for atomicity
        const syncedUpdates = filterSyncedTasks(tasksToUpdate).map(t => ({
          taskId: t.id,
          data: { y: t.y }
        }));
        
        const firebaseTaskId = await firebaseServices.createTaskWithRelationships(
          cleanedData as Omit<Task, 'id'>,
          { taskId: parentId, data: { childBottomId: 'PLACEHOLDER' } },
          syncedUpdates
        );
        return firebaseTaskId;
      }
    };
    
    // Success callback
    const onSuccess = (firebaseTaskId: string) => {
      set(state => ({
        tasks: state.tasks.map(t => 
          t.id === newNode.id 
            ? markTaskAsSynced(t, firebaseTaskId)
            : t
        )
      }));
    };
    
    // Rollback
    const rollback = () => {
      set({ tasks: originalTasks });
    };
    
    await withOptimisticUpdate(
      operationId,
      optimisticUpdate,
      firebaseOperation,
      rollback,
      onSuccess
    );
  },

  addTaskHorizontal: async (parentId, dir) => {
    const state = get();
    const parent = state.tasks.find(t => t.id === parentId);
    if (!parent) return;
    
    const isTemplate = !!parent.templateId && !parent.projectId;
    const operationId = generateId('op');
    
    // Only check the parent's own descendants for obstacles (not the entire connected graph)
    const parentDescIds = getDescendants(state.tasks, parentId);
    const parentSubtree = state.tasks.filter(t => t.id === parentId || parentDescIds.has(t.id));
    const existingChildIds = dir === 1 ? parent.childRightIds : parent.childLeftIds;
    const existingChildId = existingChildIds?.[0];
    let insertionX: number;
    let tasksToUpdate: Task[] = [];
    
    // Calculate insertion position
    if (existingChildId) {
      insertionX = parent.x + dir;
    } else {
      const obstaclesBelow = parentSubtree.filter(t => 
        t.y < parent.y && ((dir === 1 && t.x > parent.x) || (dir === -1 && t.x < parent.x))
      );
      if (obstaclesBelow.length > 0) {
        const outermostX = dir === 1 
          ? Math.max(...obstaclesBelow.map(o => o.x)) 
          : Math.min(...obstaclesBelow.map(o => o.x));
        insertionX = outermostX + dir;
      } else {
        insertionX = parent.x + dir;
      }
    }

    // Shift siblings AND their entire subtrees to make room
    const shifted = new Set<string>();
    const scopeTasks = state.tasks.filter(t =>
      t.phaseId === parent.phaseId && t.parentId === parent.parentId
    );
    const shiftAmount = dir === 1 ? 1 : -1;
    scopeTasks.forEach(t => {
      const shouldShift = (dir === 1 && t.x >= insertionX) ||
                          (dir === -1 && t.x <= insertionX);
      if (shouldShift && !shifted.has(t.id)) {
        shifted.add(t.id);
        tasksToUpdate.push({ ...t, x: t.x + shiftAmount });
        // Cascade shift to all descendants of this sibling
        const descIds = getDescendants(state.tasks, t.id);
        state.tasks.forEach(d => {
          if (descIds.has(d.id) && !shifted.has(d.id)) {
            shifted.add(d.id);
            tasksToUpdate.push({ ...d, x: d.x + shiftAmount });
          }
        });
      }
    });
    
    const uniqueTitle = generateUniqueName("New Parallel Task", state.tasks, { 
      projectId: parent.projectId, 
      phaseId: parent.phaseId, 
      parentId: parent.parentId 
    });
    
    const newNode = createUnsyncedTask('nt', {
      x: insertionX, 
      y: parent.y, 
      phaseId: parent.phaseId, 
      projectId: parent.projectId, 
      templateId: parent.templateId, 
      parentId: parent.parentId,
      childLeftIds: dir === -1 && existingChildId ? [existingChildId] : [], 
      childRightIds: dir === 1 && existingChildId ? [existingChildId] : [], 
      audience: parent.audience || 'Both',
      title: uniqueTitle, 
      status: 'NOT_STARTED', 
      isCritical: false, 
      dependsOn: parent.dependsOn || [], 
      lastActivityAt: Date.now(), 
      duration: 1, 
      daysToComplete: 1, 
      visibility: TaskVisibility.PUBLIC
    });
    
    // Store original state for rollback
    const originalTasks = [...state.tasks];
    
    // Optimistic update
    const optimisticUpdate = () => {
      set(state => ({
        tasks: [
          ...state.tasks.map(t => {
            if (t.id === parentId) {
              const next = { ...t };
              if (dir === 1) next.childRightIds = [newNode.id];
              else next.childLeftIds = [newNode.id];
              return next;
            }
            const updated = tasksToUpdate.find(tu => tu.id === t.id);
            return updated || t;
          }),
          newNode
        ]
      }));
    };
    
    // Firebase operation
    const firebaseOperation = async () => {
      if (state.mode !== 'firebase') return newNode.id;
      
      const { id, _firebaseSynced, _localOnly, ...taskData } = newNode;
      const cleanedData = cleanTaskForFirebase(taskData);
      
      if (isTemplate) {
        // Template: create task + update parent + update synced task positions
        const firebaseTaskId = await firebaseServices.createTask(cleanedData as Omit<Task, 'id'>);
        
        const parentUpdates = dir === 1 
          ? { childRightIds: [firebaseTaskId] }
          : { childLeftIds: [firebaseTaskId] };
        
        // Filter to only synced tasks to avoid "document not found" errors
        const syncedUpdates = filterSyncedTasks(tasksToUpdate);
        
        if (syncedUpdates.length > 0) {
          // Batch update parent + synced position changes
          await firebaseServices.batchUpdateTasks([
            { taskId: parentId, data: parentUpdates },
            ...syncedUpdates.map(t => ({ taskId: t.id, data: { x: t.x } }))
          ]);
        } else {
          // Just update parent if no synced siblings need position changes
          await firebaseServices.updateTask(parentId, parentUpdates);
        }
        
        return firebaseTaskId;
      } else {
        // Project: use transaction for atomicity
        const syncedUpdates = filterSyncedTasks(tasksToUpdate).map(t => ({
          taskId: t.id,
          data: { x: t.x }
        }));
        
        const parentField = dir === 1 ? 'childRightIds' : 'childLeftIds';
        const firebaseTaskId = await firebaseServices.createTaskWithRelationships(
          cleanedData as Omit<Task, 'id'>,
          { taskId: parentId, data: { [parentField]: ['PLACEHOLDER'] } },
          syncedUpdates
        );
        return firebaseTaskId;
      }
    };
    
    // Success callback
    const onSuccess = (firebaseTaskId: string) => {
      set(state => ({
        tasks: state.tasks.map(t => 
          t.id === newNode.id 
            ? markTaskAsSynced(t, firebaseTaskId)
            : t
        )
      }));
    };
    
    // Rollback
    const rollback = () => {
      set({ tasks: originalTasks });
    };
    
    await withOptimisticUpdate(
      operationId,
      optimisticUpdate,
      firebaseOperation,
      rollback,
      onSuccess
    );
  },

  addGlobalParallelTask: async (projectId, phaseId, parentTaskId, templateId) => {
    const state = get();
    const operationId = generateId('op');
    
    const relatedTasks = state.tasks.filter(t => t.phaseId === phaseId && t.parentId === parentTaskId);
    const maxX = relatedTasks.length > 0 ? Math.max(...relatedTasks.map(t => t.x)) : -1;
    const targetX = maxX + 1;
    const uniqueTitle = generateUniqueName("New Origin Node", state.tasks, { 
      projectId, 
      phaseId: phaseId!, 
      parentId: parentTaskId 
    });
    const start = new Date().toISOString().split('T')[0];
    
    const newNode = createUnsyncedTask('nt', {
      x: targetX, 
      y: 0, 
      parentId: parentTaskId, 
      childLeftIds: [], 
      childRightIds: [], 
      title: uniqueTitle, 
      audience: 'Both',
      status: 'NOT_STARTED', 
      phaseId: phaseId!, 
      projectId, 
      templateId, 
      isCritical: false, 
      dependsOn: [], 
      startDate: start, 
      endDate: start, 
      lastActivityAt: Date.now(), 
      duration: 1, 
      daysToComplete: 1, 
      visibility: TaskVisibility.PUBLIC
    });
    
    // Store original state for rollback
    const originalTasks = [...state.tasks];
    
    // Optimistic update
    const optimisticUpdate = () => {
      set(state => ({
        tasks: [...state.tasks, newNode]
      }));
    };
    
    // Firebase operation
    const firebaseOperation = async () => {
      if (state.mode !== 'firebase') {
        get().addNotification(`⚠️ Running in MOCK mode - task not saved to Firebase`, 'warning');
        return newNode.id;
      }
      
      const { id, _firebaseSynced, _localOnly, ...taskData } = newNode;
      const cleanedData = cleanTaskForFirebase(taskData);
      const firebaseTaskId = await firebaseServices.createTask(cleanedData as Omit<Task, 'id'>);
      
      get().addNotification(
        `✅ Saved (ID: ${firebaseTaskId.substring(0, 8)})`, 
        'success'
      );
      
      return firebaseTaskId;
    };
    
    // Success callback
    const onSuccess = (firebaseTaskId: string) => {
      set(state => ({
        tasks: state.tasks.map(t => 
          t.id === newNode.id 
            ? markTaskAsSynced(t, firebaseTaskId)
            : t
        )
      }));
    };
    
    // Rollback
    const rollback = () => {
      set({ tasks: originalTasks });
    };
    
    await withOptimisticUpdate(
      operationId,
      optimisticUpdate,
      firebaseOperation,
      rollback,
      onSuccess
    );
  },

  addTicket: async (ticket) => {
    const state = get();
    let ticketId: string;
    
    if (state.mode === 'firebase') {
      try {
        ticketId = await firebaseServices.createTicket(ticket);
      } catch (error: any) {
        state.addNotification(error.message || 'Failed to create ticket', 'error');
        return;
      }
    } else {
      ticketId = generateId('tk');
    }
    
    const newTicket: Ticket = { ...ticket, id: ticketId, status: 'OPEN', createdAt: Date.now(), updatedAt: Date.now() };
    set(state => ({ tickets: [newTicket, ...state.tickets] }));
    get().addNotification("Request created", "success");
  },

  updateTicket: async (ticketId, updates) => {
    const state = get();
    
    if (state.mode === 'firebase') {
      try {
        await firebaseServices.updateTicket(ticketId, updates);
      } catch (error: any) {
        state.addNotification(error.message || 'Failed to update ticket', 'error');
        return;
      }
    }
    
    set(state => ({ 
        tickets: state.tickets.map(t => t.id === ticketId ? { ...t, ...updates, updatedAt: Date.now() } : t) 
    }));
  },

  advancePhase: async (phaseId) => {
    const state = get();
    const current = state.phases.find(p => p.id === phaseId);
    if (!current) return;
    
    const projectPhases = state.phases.filter(p => p.projectId === current.projectId);
    const currentIndex = projectPhases.findIndex(p => p.id === phaseId);
    const nextPhase = projectPhases[currentIndex + 1];

    const updatedPhases = state.phases.map(p => {
        if (p.id === phaseId) return { ...p, status: PhaseStatus.COMPLETED, gatePassed: true };
        if (nextPhase && p.id === nextPhase.id) return { ...p, status: PhaseStatus.ACTIVE };
        return p;
    });

    const project = state.projects.find(p => state.phases.find(ph => ph.id === phaseId)?.projectId === p.id);
    let updatedProjects = state.projects;

    if (project) {
        if (nextPhase) {
            updatedProjects = updatedProjects.map(p => p.id === project.id ? { ...p, activePhaseId: nextPhase.id } : p);
        } else {
            updatedProjects = updatedProjects.map(p => p.id === project.id ? { ...p, status: 'Green', progress: 100 } : p);
        }
    }
    
    // Persist to Firebase if in firebase mode (use transaction for atomicity)
    if (state.mode === 'firebase') {
      try {
        const projectUpdates: Partial<Project> = project 
          ? (nextPhase ? { activePhaseId: nextPhase.id } : { status: 'Green' as const, progress: 100 })
          : {};
        
        await firebaseServices.advancePhaseTransaction(
          phaseId,
          nextPhase?.id,
          project?.id,
          projectUpdates
        );
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to advance phase', 'error');
        return;
      }
    }
    
    set({ phases: updatedPhases as Phase[], projects: updatedProjects });
    
    if (nextPhase) {
      get().addNotification("Project advanced to next phase", "success");
    } else {
      get().addNotification("Project completed!", "success");
    }
  },

  addProjectPhase: async (projectId: string, phaseName?: string) => {
    const state = get();
    const project = state.projects.find(p => p.id === projectId);
    if (!project) {
      state.addNotification('Project not found', 'error');
      return;
    }
    
    const projectPhases = state.phases.filter(p => p.projectId === projectId);
    
    // Generate unique name if not provided or if name already exists
    let uniqueName = phaseName || 'New Phase';
    if (projectPhases.some(p => p.name === uniqueName)) {
      let counter = 1;
      while (projectPhases.some(p => p.name === `${uniqueName} ${counter}`)) {
        counter++;
      }
      uniqueName = `${uniqueName} ${counter}`;
    }
    
    const newPhase: Phase = {
      id: generateId('ph'),
      name: uniqueName,
      status: PhaseStatus.NOT_STARTED,
      projectId,
      gatePassed: false,
      isPrivate: false
    };
    
    // Persist to Firebase if in firebase mode
    if (state.mode === 'firebase') {
      try {
        const { id, ...phaseData } = newPhase;
        const cleanedPhaseData = removeUndefinedFields(phaseData);
        const firebasePhaseId = await firebaseServices.createPhase(cleanedPhaseData as Omit<Phase, 'id'>);
        newPhase.id = firebasePhaseId;
        state.addNotification(`Phase "${uniqueName}" added`, 'success');
      } catch (error: any) {
        state.addNotification(error.message || 'Failed to add phase', 'error');
        return;
      }
    } else {
      state.addNotification(`Phase "${uniqueName}" added`, 'success');
    }
    
    set(state => ({ phases: [...state.phases, newPhase] }));
  },

  addNotification: (message, type = 'info') => {
    const id = Date.now().toString();
    set(state => ({ notifications: [...state.notifications, { id, message, type, timestamp: Date.now() }] }));
    setTimeout(() => get().removeNotification(id), 5000);
  },

  removeNotification: (id) => set(state => ({ notifications: state.notifications.filter(n => n.id !== id) })),

  reorganize: (projectId?: string, phaseId?: string, templateId?: string, parentTaskId?: string) => {
    const state = get();
    // Determine the scope: which phase to relayout
    const targetPhaseId = phaseId || 
      (projectId ? state.projects.find(p => p.id === projectId)?.activePhaseId : undefined) ||
      (templateId ? state.templates.find(t => t.id === templateId)?.phases[0]?.id : undefined);
    
    if (!targetPhaseId) {
      get().addNotification("No phase found to reorganize", "error");
      return;
    }

    const updatedTasks = relayoutScope(state.tasks, targetPhaseId, parentTaskId);
    
    // Check if any positions actually changed
    const changes = updatedTasks.filter(t => {
      const orig = state.tasks.find(o => o.id === t.id);
      return orig && (orig.x !== t.x || orig.y !== t.y);
    });

    if (changes.length === 0) {
      get().addNotification("Layout is already optimal", "info");
      return;
    }

    set({ tasks: updatedTasks });

    // Persist position changes to Firebase
    if (state.mode === 'firebase') {
      const positionUpdates = changes
        .filter(t => isTaskSynced(t))
        .map(t => ({ taskId: t.id, data: { x: t.x, y: t.y } }));
      if (positionUpdates.length > 0) {
        firebaseServices.batchUpdateTasks(positionUpdates).catch(err =>
          console.error('Failed to persist reorganize:', err)
        );
      }
    }

    // Invalidate task cache
    state.cache.tasks.forEach((entry, key) => {
      if (key.includes(targetPhaseId) || key.includes(projectId || '') || key.includes(templateId || '')) {
        entry.data = updatedTasks.filter(t => entry.data.some(cached => cached.id === t.id));
        entry.timestamp = Date.now();
      }
    });

    get().addNotification(`Reorganized ${changes.length} nodes`, "success");
  },

  addUser: (userData) => {
    const newUser = { ...userData, id: generateId('u'), emailVerified: false };
    set(state => ({ users: [...state.users, newUser] }));
    return newUser;
  },

  approveUser: (pendingUserId, config) => {
    set(state => {
        const pending = state.pendingUsers.find(p => p.id === pendingUserId);
        if (!pending) return {};

        const newUser: User = {
            id: generateId('u'),
            name: pending.name,
            email: pending.email,
            emailVerified: false,
            avatar: pending.avatar,
            role: config.role,
            company: config.company,
            department: config.department,
            assignedProjectIds: config.assignedProjectIds
        };

        get().addNotification(`User ${pending.name} provisioned successfully`, "success");
        return { users: [...state.users, newUser], pendingUsers: state.pendingUsers.filter(p => p.id !== pendingUserId) };
    });
  },

  rejectUser: (pendingUserId) => {
    set(state => ({ pendingUsers: state.pendingUsers.filter(p => p.id !== pendingUserId) }));
    get().addNotification("User request rejected", "info");
  },

  addTemplate: async (title, description) => {
    // Failsafe: Check for duplicate names (case-insensitive)
    const existingTemplate = get().templates.find(
      t => t.title.toLowerCase().trim() === title.toLowerCase().trim()
    );
    
    if (existingTemplate) {
      get().addNotification(
        `Blueprint "${title}" already exists`, 
        'error'
      );
      return ''; // Return empty string to indicate failure
    }
    
    const defaultPhases = [
      { id: generateId('tph'), name: 'Design', sequence: 1 },
      { id: generateId('tph'), name: 'Build', sequence: 2 },
      { id: generateId('tph'), name: 'Production', sequence: 3 },
      { id: generateId('tph'), name: 'Update', sequence: 4 }
    ];

    const templateData: Omit<Template, 'id' | 'createdAt' | 'updatedAt'> = {
      title,
      description,
      version: '1.0',
      brandOnly: true,
      phases: defaultPhases,
      placeholders: ['ProductName'],
    };

    let templateId: string;
    
    // Persist to Firebase if in firebase mode
    if (get().mode === 'firebase') {
      try {
        templateId = await firebaseServices.createTemplate(templateData);
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to create template', 'error');
        return '';
      }
    } else {
      templateId = generateId('tpl');
    }

    const newTemplate: Template = {
      ...templateData,
      id: templateId,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    
    const initialTasks: Task[] = defaultPhases.map(ph => ({
      id: generateId('t'),
      x: 0, y: 0,
      childLeftIds: [], childRightIds: [],
      title: 'Start Node',
      status: 'NOT_STARTED',
      phaseId: ph.id,
      templateId: templateId,
      isCritical: false,
      duration: 1,
      daysToComplete: 1,
      lastActivityAt: Date.now(),
      audience: 'Both',
      visibility: TaskVisibility.PUBLIC
    }));

    // Save tasks to Firebase if in firebase mode
    if (get().mode === 'firebase') {
      try {
        // Create tasks and capture Firebase IDs
        const firebaseTaskIds: string[] = [];
        for (const task of initialTasks) {
          const { id, ...taskData } = task;
          const cleanedData = cleanTaskForFirebase(taskData);
          const firebaseId = await firebaseServices.createTask(cleanedData as Omit<Task, 'id'>);
          firebaseTaskIds.push(firebaseId);
        }
        
        // Update task objects with Firebase IDs and mark as synced
        initialTasks.forEach((task, index) => {
          task.id = firebaseTaskIds[index];
          task._firebaseSynced = true;
        });
      } catch (error: any) {
        console.error('Failed to create template tasks:', error);
        get().addNotification('Template created but some tasks failed to save', 'warning');
      }
    }

    set(state => ({ templates: [...state.templates, newTemplate], tasks: [...state.tasks, ...initialTasks] }));
    get().addNotification("Blueprint created successfully", "success");
    return templateId;
  },

  updateTemplate: async (templateId, updates) => {
    // If updating title, check for duplicates
    if (updates.title) {
      const trimmedTitle = updates.title.trim();
      const duplicate = get().templates.find(
        t => t.id !== templateId && 
             t.title.toLowerCase().trim() === trimmedTitle.toLowerCase()
      );
      
      if (duplicate) {
        get().addNotification(
          `Blueprint "${trimmedTitle}" already exists`, 
          'error'
        );
        return;
      }
    }
    
    if (get().mode === 'firebase') {
      try {
        await firebaseServices.updateTemplate(templateId, updates);
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to update template', 'error');
        return;
      }
    }
    
    set(state => ({
      templates: state.templates.map(t => 
        t.id === templateId ? { ...t, ...updates, updatedAt: Date.now() } : t
      )
    }));
  },

  deleteTemplate: async (templateId) => {
    if (get().mode === 'firebase') {
      try {
        // Delete template and all associated tasks atomically
        const templateTasks = get().tasks.filter(t => t.templateId === templateId);
        const taskIds = templateTasks.map(t => t.id);
        
        await firebaseServices.deleteTemplate(templateId);
        
        // Batch delete all template tasks
        if (taskIds.length > 0) {
          await firebaseServices.batchDeleteTasks(taskIds);
        }
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to delete template', 'error');
        return;
      }
    }
    
    set(state => ({
      templates: state.templates.filter(t => t.id !== templateId),
      tasks: state.tasks.filter(t => t.templateId !== templateId)
    }));
    get().addNotification("Blueprint deleted", "info");
  },

  addTemplatePhase: async (templateId) => {
    const state = get();
    const template = state.templates.find(t => t.id === templateId);
    if (!template) return;

    const nextSeq = Math.max(0, ...template.phases.map(p => p.sequence)) + 1;
    const newPhaseId = generateId('tph');
    const newPhase: TemplatePhase = { id: newPhaseId, name: 'New Phase', sequence: nextSeq };
    
    const newTask: Task = {
        id: generateId('t'),
        x: 0, y: 0,
        childLeftIds: [], childRightIds: [],
        title: 'Start Node',
        status: 'NOT_STARTED',
        phaseId: newPhaseId,
        templateId: templateId,
        projectId: null, // Explicitly null for template tasks
        isCritical: false,
        duration: 1,
        daysToComplete: 1,
        lastActivityAt: Date.now(),
        audience: 'Both',
        visibility: TaskVisibility.PUBLIC
    };

    // Persist to Firebase if in firebase mode
    if (state.mode === 'firebase') {
      try {
        // Update template with new phase
        const updatedPhases = [...template.phases, newPhase];
        await firebaseServices.updateTemplate(templateId, { phases: updatedPhases });
        
        // Create the start node task
        const { id, ...taskData } = newTask;
        const cleanedData = cleanTaskForFirebase(taskData);
        const firebaseTaskId = await firebaseServices.createTask(cleanedData as Omit<Task, 'id'>);
        newTask.id = firebaseTaskId;
        
        get().addNotification("Phase added with start node", "success");
      } catch (error: any) {
        console.error('Failed to create template phase:', error);
        get().addNotification(`Failed to save phase: ${error.message}`, 'error');
        return;
      }
    }

    const updatedTemplates = state.templates.map(t => t.id === templateId ? { ...t, phases: [...t.phases, newPhase] } : t);
    set({ templates: updatedTemplates, tasks: [...state.tasks, newTask] });
  },

  applyTemplate: async (templateId, projectName, fieldValues) => {
    const state = get();
    const template = state.templates.find(t => t.id === templateId);
    if (!template) return;

    // Check for duplicate project name (server-side validation)
    if (isDuplicateProjectName(projectName, state.projects)) {
      state.addNotification(
        `A project named "${projectName}" already exists. Please choose a different name.`,
        'error'
      );
      return;
    }

    const projectId = generateId('p');
    const newProject: Project = {
        id: projectId,
        name: projectName,
        code: projectName.substring(0, 3).toUpperCase() + new Date().getFullYear().toString().substr(2),
        brand: 'LuxeFashion',
        factory: fieldValues['Factory'] || 'GlobalTex',
        status: 'Green',
        progress: 0,
        startDate: new Date().toISOString().split('T')[0],
        launchDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 180).toISOString().split('T')[0],
        activePhaseId: '',
        isPrivate: false
    };

    const newPhases: Phase[] = template.phases.map(tp => ({
        id: generateId('ph'),
        name: tp.name,
        status: tp.sequence === 1 ? PhaseStatus.ACTIVE : PhaseStatus.NOT_STARTED,
        projectId,
        templateId: tp.id,
        gatePassed: false,
        isPrivate: false
    }));

    newProject.activePhaseId = newPhases.find(p => p.status === PhaseStatus.ACTIVE)?.id || newPhases[0].id;

    const templateTasks = state.tasks.filter(t => t.templateId === templateId);
    const newTasks: Task[] = [];
    
    if (templateTasks.length > 0) {
        templateTasks.forEach(tt => {
            const tPhase = template.phases.find(p => p.id === tt.phaseId);
            const newPhase = newPhases.find(np => np.name === tPhase?.name);
            
            if (newPhase) {
                // Match roles to users
                let assigneeIds: string[] = [];
                if (tt.assignedRoleIds && tt.assignedRoleIds.length > 0) {
                    const assignedRole = state.projectRoles.find(r => r.id === tt.assignedRoleIds![0]);
                    if (assignedRole) {
                        // Find users matching the role criteria
                        const matchingUsers = state.users.filter(u => {
                            // Match by organization
                            if (assignedRole.organization === 'Brand' && u.role === Role.FACTORY) return false;
                            if (assignedRole.organization === 'Factory' && u.role !== Role.FACTORY) return false;
                            
                            // Match by team if specified
                            if (assignedRole.team && u.department !== assignedRole.team) return false;
                            
                            return true;
                        });
                        
                        // Assign first matching user or leave unassigned
                        if (matchingUsers.length > 0) {
                            assigneeIds = [matchingUsers[0].id];
                        }
                    }
                }
                
                newTasks.push({
                    ...tt,
                    id: generateId('t'),
                    projectId,
                    phaseId: newPhase.id,
                    templateId: undefined,
                    parentId: undefined,
                    status: 'NOT_STARTED',
                    startDate: undefined,
                    endDate: undefined,
                    assignedRoleIds: undefined, // Clear template role assignments
                    assigneeIds, // Set matched user assignments
                    comments: [],
                    visibility: TaskVisibility.PUBLIC
                });
            }
        });
    } else {
        newTasks.push({
            id: generateId('t'),
            x: 0, y: 0,
            childLeftIds: [], childRightIds: [],
            title: 'Project Kickoff',
            status: 'NOT_STARTED',
            phaseId: newPhases[0].id,
            projectId,
            isCritical: true,
            duration: 1,
            daysToComplete: 1,
            lastActivityAt: Date.now(),
            audience: 'Both',
            visibility: TaskVisibility.PUBLIC
        });
    }
    
    // Persist to Firebase if in firebase mode
    if (state.mode === 'firebase') {
      try {
        // Create project - remove undefined fields before sending to Firebase
        const { id: _, ...projectData } = newProject;
        const cleanedProjectData = removeUndefinedFields(projectData);
        const firebaseProjectId = await firebaseServices.createProject(cleanedProjectData as Omit<Project, 'id'>);
        
        // Update IDs to match Firebase IDs
        newProject.id = firebaseProjectId;
        newPhases.forEach(ph => { ph.projectId = firebaseProjectId; });
        newTasks.forEach(t => { t.projectId = firebaseProjectId; });
        
        // Batch create phases
        const phasesToCreate = newPhases.map(phase => {
          const { id, ...phaseData } = phase;
          return phaseData;
        });
        const firebasePhaseIds = await firebaseServices.batchCreatePhases(phasesToCreate);
        
        // Create phase ID mapping
        const phaseIdMap = new Map<string, string>();
        newPhases.forEach((phase, index) => {
          phaseIdMap.set(phase.id, firebasePhaseIds[index]);
          newPhases[index].id = firebasePhaseIds[index];
        });
        
        // Update project activePhaseId
        if (newProject.activePhaseId) {
          const mappedActivePhaseId = phaseIdMap.get(newProject.activePhaseId);
          if (mappedActivePhaseId) {
            newProject.activePhaseId = mappedActivePhaseId;
          }
        }
        
        // Update task phaseIds to use Firebase IDs
        newTasks.forEach(t => {
          const mappedPhaseId = phaseIdMap.get(t.phaseId);
          if (mappedPhaseId) {
            t.phaseId = mappedPhaseId;
          }
        });
        
        // Batch create tasks
        const tasksToCreate = newTasks.map(task => {
          const { id, ...taskData } = task;
          return cleanTaskForFirebase(taskData);
        });
        const firebaseTaskIds = await firebaseServices.batchCreateTasks(
          tasksToCreate as Array<Omit<Task, 'id'>>
        );
        
        // Update task IDs
        newTasks.forEach((task, index) => {
          newTasks[index].id = firebaseTaskIds[index];
          newTasks[index]._firebaseSynced = true;
        });
        
        // Update project with final activePhaseId
        await firebaseServices.updateProject(firebaseProjectId, { activePhaseId: newProject.activePhaseId });
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to create project', 'error');
        return;
      }
    }
    
    set(state => ({
        projects: deduplicateProjects([...state.projects, newProject]),
        phases: [...state.phases, ...newPhases], 
        tasks: [...state.tasks, ...newTasks] 
    }));
    get().addNotification(`Project "${projectName}" created from blueprint`, "success");
  },

  createBlankProject: async (data: { name: string; pmId: string; factory?: string; goalLaunchDate?: string }) => {
    const state = get();
    if (!state.user) {
      throw new Error('User must be logged in to create a project');
    }

    // Check for duplicate project name (server-side validation)
    if (isDuplicateProjectName(data.name, state.projects)) {
      throw new Error(`A project named "${data.name}" already exists. Please choose a different name.`);
    }

    // Auto-generate project code from name
    const code = data.name.substring(0, 3).toUpperCase() + new Date().getFullYear().toString().substr(2);
    
    // Create project data
    const projectData: Omit<Project, 'id'> = {
      name: data.name,
      code,
      brand: state.user.company || 'Brand',
      factory: data.factory,
      brandPMId: data.pmId,
      status: 'Green',
      progress: 0,
      startDate: new Date().toISOString().split('T')[0],
      launchDate: data.goalLaunchDate || '', // Goal launch date or empty (will be calculated from tasks)
      activePhaseId: '', // Will be set after phase creation
      isPrivate: false
    };

    let projectId: string;
    let phaseId: string;

    // Create in Firebase or mock
    if (state.mode === 'firebase') {
      try {
        // Create project - remove undefined fields before sending to Firebase
        const cleanedProjectData = removeUndefinedFields(projectData);
        projectId = await firebaseServices.createProject(cleanedProjectData as Omit<Project, 'id'>);
        
        // Create default phase
        const phaseData: Omit<Phase, 'id'> = {
          name: 'Planning',
          status: PhaseStatus.ACTIVE,
          projectId,
          gatePassed: false,
          isPrivate: false
        };
        phaseId = await firebaseServices.createPhase(phaseData);
        
        // Update project with activePhaseId
        await firebaseServices.updateProject(projectId, { activePhaseId: phaseId });
        
        // Create initial task
        const taskData: Omit<Task, 'id'> = {
          x: 0, y: 0,
          childLeftIds: [], childRightIds: [],
          title: 'Project Kickoff',
          status: 'NOT_STARTED',
          phaseId,
          projectId,
          isCritical: false,
          duration: 1,
          daysToComplete: 1,
          lastActivityAt: Date.now(),
          audience: 'Both',
          visibility: TaskVisibility.PUBLIC
        };
        await firebaseServices.createTask(taskData);
        
        // Reload data to get the new project
        await state.loadUserData();
        
        state.addNotification(`Project "${data.name}" created successfully`, 'success');
        return projectId;
      } catch (error: any) {
        state.addNotification(error.message || 'Failed to create project', 'error');
        throw error;
      }
    } else {
      // Mock mode implementation
      projectId = generateId('p');
      phaseId = generateId('ph');
      
      const newProject: Project = {
        ...projectData,
        id: projectId,
        activePhaseId: phaseId
      };
      
      const newPhase: Phase = {
        id: phaseId,
        name: 'Planning',
        status: PhaseStatus.ACTIVE,
        projectId,
        gatePassed: false,
        isPrivate: false
      };
      
      const newTask: Task = {
        id: generateId('t'),
        x: 0, y: 0,
        childLeftIds: [], childRightIds: [],
        title: 'Project Kickoff',
        status: 'NOT_STARTED',
        phaseId,
        projectId,
        isCritical: false,
        duration: 1,
        daysToComplete: 1,
        lastActivityAt: Date.now(),
        audience: 'Both',
        visibility: TaskVisibility.PUBLIC
      };
      
      set(state => ({
        projects: deduplicateProjects([...state.projects, newProject]),
        phases: [...state.phases, newPhase],
        tasks: [...state.tasks, newTask]
      }));
      
      state.addNotification(`Project "${data.name}" created successfully`, 'success');
      return projectId;
    }
  },

  toggleFollowProject: async (projectId) => {
    const newFollowedIds = get().followedProjectIds.includes(projectId) 
      ? get().followedProjectIds.filter(id => id !== projectId) 
      : [...get().followedProjectIds, projectId];
    
    set({ followedProjectIds: newFollowedIds });
    
    // Persist to user document
    if (get().mode === 'firebase' && get().user) {
      try {
        await firebaseServices.updateUser(get().user!.id, { 
          followedProjectIds: newFollowedIds 
        });
      } catch (error: any) {
        console.error('Failed to update followed projects:', error);
      }
    }
  },

  updateAlertPrefs: async (prefs) => {
    const newPrefs = { ...get().alertPrefs, ...prefs };
    set({ alertPrefs: newPrefs });
    
    // Persist to user document
    if (get().mode === 'firebase' && get().user) {
      try {
        await firebaseServices.updateUser(get().user!.id, { alertPrefs: newPrefs });
      } catch (error: any) {
        console.error('Failed to update alert preferences:', error);
      }
    }
  },

  setThemeMode: async (mode) => {
    set({ themeMode: mode });
    
    // Persist to user document
    if (get().mode === 'firebase' && get().user) {
      try {
        await firebaseServices.updateUser(get().user!.id, { themeMode: mode });
      } catch (error: any) {
        console.error('Failed to update theme mode:', error);
      }
    }
  },

  regenerateSyncToken: () => set(state => {
      if (!state.user) return {};
      const newToken = 'st-' + state.user.name.split(' ')[0].toLowerCase() + '-' + Math.random().toString(36).substring(7);
      const updatedUser = { ...state.user, calendarSyncToken: newToken };
      return { 
          user: updatedUser, 
          users: state.users.map(u => u.id === state.user?.id ? updatedUser : u) 
      };
  }),

  markCommentAsRead: async (taskId, commentId) => {
    const state = get();
    
    if (state.mode === 'firebase') {
      try {
        await firebaseServices.updateTaskComment(taskId, commentId, { isRead: true });
      } catch (error: any) {
        console.error('Failed to update comment:', error);
      }
    }
    
    set(state => ({
      tasks: state.tasks.map(t => t.id === taskId ? {
        ...t,
        comments: t.comments?.map(c => c.id === commentId ? { ...c, isRead: true } : c)
      } : t)
    }));
  },

  replyToComment: async (taskId, originalCommentId, replyText) => {
    const state = get();
    if (!state.user) return;
    
    const newComment: Comment = {
      id: generateId('c'), authorId: state.user.id, authorName: state.user.name, text: replyText, timestamp: Date.now(), isRead: true
    };
    
    if (state.mode === 'firebase') {
      try {
        await firebaseServices.addCommentToTask(taskId, newComment);
      } catch (error: any) {
        state.addNotification(error.message || 'Failed to add comment', 'error');
        return;
      }
    }
    
    set(state => ({ 
      tasks: state.tasks.map(t => t.id === taskId ? { ...t, comments: [...(t.comments || []), newComment] } : t) 
    }));
  },

  nudgeTask: (taskId) => {
    set(state => ({ tasks: state.tasks.map(t => t.id === taskId ? { ...t, lastNudgedAt: Date.now() } : t) }));
    get().addNotification("Nudge sent to assignee", "info");
  },

  dismissFollowUp: (taskId) => set(state => ({
    tasks: state.tasks.map(t => t.id === taskId ? { ...t, followUpDismissedAt: Date.now() } : t)
  })),

  checkAccess: (projectId: string) => {
      const state = get();
      if (!state.user) return false;
      if (state.user.role === Role.ADMIN || state.user.role === Role.PM || state.user.role === Role.BRAND) {
          return true;
      }
      if (state.user.role === Role.FACTORY) {
          const project = state.projects.find(p => p.id === projectId);
          return !!(project && project.factory === state.user.company && !project.isPrivate);
      }
      return false;
  },

  addCalendar: async (name) => {
    const user = get().user;
    if (!user) return '';
    
    const newCal: Omit<CustomCalendar, 'id' | 'updatedAt'> = {
      name, 
      description: '', 
      creatorId: user.id, 
      editorIds: [], 
      viewerIds: [], 
      events: [],
    };
    
    let calendarId: string;
    if (get().mode === 'firebase') {
      try {
        calendarId = await firebaseServices.createCustomCalendar(newCal);
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to create calendar', 'error');
        return '';
      }
    } else {
      calendarId = generateId('cal');
    }
    
    const newCalendar = { ...newCal, id: calendarId, updatedAt: Date.now() };
    set(state => ({ customCalendars: [...state.customCalendars, newCalendar] }));
    get().addNotification(`Calendar "${name}" created`, "success");
    return calendarId;
  },

  updateCalendar: async (id, updates) => {
    if (get().mode === 'firebase') {
      try {
        await firebaseServices.updateCustomCalendar(id, updates);
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to update calendar', 'error');
        return;
      }
    }
    
    set(state => ({
      customCalendars: state.customCalendars.map(c => 
        c.id === id ? { ...c, ...updates, updatedAt: Date.now() } : c
      )
    }));
  },

  deleteCalendar: async (id) => {
    if (get().mode === 'firebase') {
      try {
        await firebaseServices.deleteCustomCalendar(id);
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to delete calendar', 'error');
        return;
      }
    }
    
    set(state => ({
      customCalendars: state.customCalendars.filter(c => c.id !== id),
      selectedCustomCalendarIds: state.selectedCustomCalendarIds.filter(cid => cid !== id)
    }));
  },

  toggleCalendarVisibility: async (id) => {
    const newSelectedIds = get().selectedCustomCalendarIds.includes(id) 
      ? get().selectedCustomCalendarIds.filter(cid => cid !== id) 
      : [...get().selectedCustomCalendarIds, id];
    
    set({ selectedCustomCalendarIds: newSelectedIds });
    
    // Persist to user document
    if (get().mode === 'firebase' && get().user) {
      try {
        await firebaseServices.updateUser(get().user!.id, { 
          selectedCustomCalendarIds: newSelectedIds 
        });
      } catch (error: any) {
        console.error('Failed to update calendar visibility:', error);
      }
    }
  },

  addProjectRole: async (role: Omit<ProjectRole, 'id' | 'createdAt'>) => {
    let roleId: string;
    
    if (get().mode === 'firebase') {
      try {
        roleId = await firebaseServices.createProjectRole(role);
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to create role', 'error');
        return '';
      }
    } else {
      roleId = generateId('role');
    }
    
    const newRole: ProjectRole = {
      ...role,
      id: roleId,
      createdAt: Date.now()
    };
    set(state => ({ projectRoles: [...state.projectRoles, newRole] }));
    get().addNotification(`Role "${role.name}" created`, 'success');
    return roleId;
  },

  updateProjectRole: async (roleId: string, updates: Partial<ProjectRole>) => {
    if (get().mode === 'firebase') {
      try {
        await firebaseServices.updateProjectRole(roleId, updates);
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to update role', 'error');
        return;
      }
    }
    
    set(state => ({
      projectRoles: state.projectRoles.map(r => r.id === roleId ? { ...r, ...updates } : r)
    }));
  },

  deleteProjectRole: async (roleId: string) => {
    if (get().mode === 'firebase') {
      try {
        await firebaseServices.deleteProjectRole(roleId);
      } catch (error: any) {
        get().addNotification(error.message || 'Failed to delete role', 'error');
        return;
      }
    }
    
    set(state => ({ projectRoles: state.projectRoles.filter(r => r.id !== roleId) }));
    get().addNotification('Role deleted', 'info');
  }
  };
});
